<template>
  <div class="growth-dashboard-container">
    <section class="dashboard">
      <div>
        <CustomLoader v-if="isLoading" :width="70"></CustomLoader>
      </div>

      <!-- Page Header -->
      <b-row class="mb-4">
        <b-col md="8">
          <div class="d-flex align-items-center">
            <div class="page-icon-wrapper mr-3">
              <i class="fa fa-balance-scale page-icon"></i>
            </div>
            <div>
              <h4 class="page-title mb-1">Outstanding Dashboard</h4>
              <p class="page-subtitle text-muted mb-0">
                <i class="fa fa-calendar-alt mr-2"></i>
                Summary and analysis of outstanding amounts
              </p>
            </div>
          </div>
        </b-col>
        <b-col md="4" class="text-right">
          <div class="page-actions">
            <b-button
              variant="outline-primary"
              class="mr-2"
              @click="refreshDashboard"
            >
              <i class="fa fa-sync-alt" :class="{ 'fa-spin': isRefreshing }"></i>
              Refresh
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Summary Cards Row -->
      <b-row class="mb-4 cards-row">
        <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-info p-0">
            <div class="card-body p-3">
              <div class="d-flex align-items-center flex-nowrap">
                <div class="card-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-2 mr-md-3">
                  <i class="fa fa-calculator"></i>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <h6 class="card-title text-muted mb-0 small text-truncate">Net Amount</h6>
                  <h5 class="mb-0 text-info text-truncate font-weight-bold currency-text">
                    {{ formatCurrency(summary.totalNetAmount) }}
                  </h5>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
         <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-warning p-0">
            <div class="card-body p-3">
              <div class="d-flex align-items-center flex-nowrap">
                <div class="card-icon bg-warning text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-2 mr-md-3">
                  <i class="fa fa-wallet"></i>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <h6 class="card-title text-muted mb-0 small text-truncate">On Account</h6>
                  <h5 class="mb-0 text-warning text-truncate font-weight-bold currency-text">
                    {{ formatCurrency(summary.totalOnAccountAmt) }}
                  </h5>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-primary p-0">
            <div class="card-body p-3">
              <div class="d-flex align-items-center flex-nowrap">
                <div class="card-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-2 mr-md-3">
                  <i class="fa fa-rupee-sign"></i>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <h6 class="card-title text-muted mb-0 small text-truncate">Outstanding</h6>
                  <h5 class="mb-0 text-primary text-truncate font-weight-bold currency-text">
                    {{ formatCurrency(summary.totalOutstanding) }}
                  </h5>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-danger p-0">
            <div class="card-body p-3">
              <div class="d-flex align-items-center flex-nowrap">
                <div class="card-icon bg-danger text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-2 mr-md-3">
                  <i class="fa fa-exclamation-triangle"></i>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <h6 class="card-title text-muted mb-0 small text-truncate">Due Amount</h6>
                  <h5 class="mb-0 text-danger text-truncate font-weight-bold currency-text">
                    {{ formatCurrency(summary.totalDueAmount) }}
                  </h5>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
       


        <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-success p-0">
            <div class="card-body p-3 text-center">
              <h6 class="card-title text-muted mb-1 small">Invoices</h6>
              <h5 class="mb-0 text-success font-weight-bold">{{ formatNumber(summary.totalInvoices) }}</h5>
            </div>
          </b-card>
        </b-col>

        <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-info-light p-0">
            <div class="card-body p-3 text-center">
              <h6 class="card-title text-muted mb-1 small">Clients</h6>
              <h5 class="mb-0 text-info font-weight-bold">{{ formatNumber(summary.totalClients) }}</h5>
            </div>
          </b-card>
        </b-col>

        <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-warning-light p-0">
            <div class="card-body p-3">
              <div class="d-flex align-items-center flex-nowrap">
                <div class="card-icon bg-warning text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-2 mr-md-3">
                  <i class="fa fa-building"></i>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <h6 class="card-title text-muted mb-0 small text-truncate">Branches</h6>
                  <h5 class="mb-0 text-warning text-truncate font-weight-bold">{{ formatNumber(summary.totalBranches) }}</h5>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-secondary p-0">
            <div class="card-body p-3">
              <div class="d-flex align-items-center flex-nowrap">
                <div class="card-icon bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-2 mr-md-3">
                  <i class="fa fa-user-tie"></i>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <h6 class="card-title text-muted mb-0 small text-truncate">Agents</h6>
                  <h5 class="mb-0 text-secondary text-truncate font-weight-bold">{{ formatNumber(summary.totalAgents) }}</h5>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col lg="3" md="4" sm="6" cols="12" class="mb-3 px-2 px-md-3">
          <b-card class="summary-card h-100 border-0 shadow-sm gradient-dark p-0">
            <div class="card-body p-3">
              <div class="d-flex align-items-center flex-nowrap">
                <div class="card-icon bg-dark text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-2 mr-md-3">
                  <i class="fa fa-ship"></i>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <h6 class="card-title text-muted mb-0 small text-truncate">Shipping</h6>
                  <h5 class="mb-0 text-dark text-truncate font-weight-bold">{{ formatNumber(summary.totalShippingLines) }}</h5>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Summary Charts Row -->
      <b-row class="mb-4">
        <b-col lg="8" class="mb-3">
          <b-card class="summary-card h-100 border-0 shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="card-title text-muted mb-0">Monthly Outstanding Trend</h6>
              <small class="text-muted" v-if="monthlyChartSeries && monthlyChartSeries[0] && monthlyChartSeries[0].data && monthlyChartSeries[0].data.length">
                Last {{ monthlyChartOptions && monthlyChartOptions.xaxis && monthlyChartOptions.xaxis.categories ? monthlyChartOptions.xaxis.categories.length : 0 }} months
              </small>
            </div>
            <div v-if="monthlyChartSeries && monthlyChartSeries[0] && monthlyChartSeries[0].data && monthlyChartSeries[0].data.length">
              <apexchart
                type="line"
                height="260"
                :options="monthlyChartOptions"
                :series="monthlyChartSeries"
              ></apexchart>
            </div>
            <div v-else class="text-muted small">
              No monthly data available.
            </div>
          </b-card>
        </b-col>

        <b-col lg="4" class="mb-3">
          <b-card class="summary-card h-100 border-0 shadow-sm">
            <h6 class="card-title text-muted mb-3">Entity Mix</h6>
            <div v-if="totalEntityCount">
              <b-progress height="18px" class="summary-entity-progress">
                <b-progress-bar
                  :value="summary.totalClients || 0"
                  :max="totalEntityCount"
                  variant="info"
                >
                  Clients
                </b-progress-bar>
                <b-progress-bar
                  :value="summary.totalAgents || 0"
                  :max="totalEntityCount"
                  variant="success"
                >
                  Agents
                </b-progress-bar>
                <b-progress-bar
                  :value="summary.totalBranches || 0"
                  :max="totalEntityCount"
                  variant="warning"
                >
                  Branches
                </b-progress-bar>
                <b-progress-bar
                  :value="summary.totalShippingLines || 0"
                  :max="totalEntityCount"
                  variant="primary"
                >
                  Shipping
                </b-progress-bar>
              </b-progress>
              <div class="d-flex justify-content-between mt-2 small text-muted">
                <span>Clients: {{ formatNumber(summary.totalClients) }}</span>
                <span>Agents: {{ formatNumber(summary.totalAgents) }}</span>
              </div>
              <div class="d-flex justify-content-between small text-muted">
                <span>Branches: {{ formatNumber(summary.totalBranches) }}</span>
                <span>Shipping: {{ formatNumber(summary.totalShippingLines) }}</span>
              </div>
            </div>
            <div v-else class="text-muted small">
              No summary data available.
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Main content: same structure as Profit/Loss Approvals (tabs + header + table + footer) -->
      <b-card
        no-body
        class="border-0 shadow-sm main-content-card"
        style="border-radius: 8px; overflow: hidden;"
      >
        <div class="tabs-scroll-container">
          <b-tabs v-model="activeTabIndex" card pills @input="onTabChange" class="premium-tabs">
            <b-tab title="Base List"></b-tab>
            <b-tab title="Client Aging"></b-tab>
            <b-tab title="All Aging"></b-tab>
            <b-tab title="Due Client Aging"></b-tab>
            <b-tab title="Year-wise"></b-tab>
            <b-tab title="Summary / Monthly"></b-tab>
            <b-tab title="Client-wise"></b-tab>
            <b-tab title="Client Monthly"></b-tab>
            <b-tab title="On Acc Client Aging"></b-tab>
            <b-tab title="Client ↔ Branch"></b-tab>
            <b-tab title="Agent-wise"></b-tab>
            <b-tab title="Agent Monthly"></b-tab>
            <b-tab title="Agent ↔ Branch"></b-tab>
            <b-tab title="Shipping Line-wise"></b-tab>
            <b-tab title="Shipping Monthly"></b-tab>
            <b-tab title="Shipping ↔ Branch"></b-tab>
            <b-tab title="Branch-wise"></b-tab>
            <b-tab title="Branch Monthly"></b-tab>
            <b-tab title="Branch ↔ Client"></b-tab>
            <b-tab title="Branch ↔ Agent"></b-tab>
            <b-tab title="Branch ↔ Shipping"></b-tab>
            <b-tab title="Assignments"></b-tab>
          </b-tabs>
        </div>

        <b-card-header class="bg-white border-bottom p-3 p-md-4">
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
            <h4 class="mb-3 mb-md-0 text-primary font-weight-bold page-inner-title">
              <i class="fa fa-check-circle mr-2"></i>Ledger Outstanding
            </h4>
            <div class="d-flex flex-wrap align-items-center action-buttons-group">
              <b-input-group class="search-input-group mb-2 mb-md-0 mr-md-3">
                <b-input-group-prepend is-text class="bg-light border-right-0">
                  <i class="fa fa-search text-muted"></i>
                </b-input-group-prepend>
                <b-form-input
                  v-model="globalSearch"
                  placeholder="Search..."
                  class="border-left-0 bg-light"
                />
                <b-input-group-append v-if="globalSearch">
                  <b-button
                    variant="link"
                    class="text-muted p-0 px-2"
                    @click="clearGlobalSearch"
                    style="text-decoration: none;"
                  >
                    <i class="fa fa-times"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              
              <div class="d-flex w-100 w-md-auto mt-2 mt-md-0 justify-content-between justify-content-md-start">
                <b-button variant="outline-primary" size="sm" @click="refreshDashboard" class="flex-grow-1 flex-md-grow-0 mr-1">
                  <i class="fa fa-refresh" :class="{ 'fa-spin': isRefreshing }"></i>
                   <span class="d-none d-sm-inline ml-1">Refresh</span>
                </b-button>
                <b-button variant="outline-success" size="sm" class="flex-grow-1 flex-md-grow-0 mr-1" @click="exportExcel">
                  <i class="fa fa-file-excel-o"></i>
                  <span class="d-none d-sm-inline ml-1">Export</span>
                </b-button>
                <b-button variant="primary" size="sm" class="flex-grow-1 flex-md-grow-0 mr-1" @click="showImportModal = true">
                  <i class="fa fa-upload"></i>
                  <span class="d-none d-sm-inline ml-1">Import</span>
                </b-button>
                <b-button variant="outline-info" size="sm" class="flex-grow-1 flex-md-grow-0 mr-1" @click="openDataUpdatesModal">
                  <i class="fa fa-history"></i>
                  <span class="d-none d-sm-inline ml-1">Data Updates</span>
                </b-button>
                <b-button v-b-toggle.ledger-filters-collapse variant="outline-secondary" size="sm" class="flex-grow-1 flex-md-grow-0">
                  <i class="fa fa-filter"></i>
                  <span class="d-none d-sm-inline ml-1">Filters</span>
                </b-button>
              </div>
            </div>
          </div>

          <b-collapse id="ledger-filters-collapse" v-model="filtersVisible">
            <b-card class="bg-light border-0 mb-3">
              <b-row>
                <b-col md="3" class="mb-2" v-if="currentTabKey === 'year-wise'">
                  <label class="small text-muted font-weight-bold">Year</label>
                  <b-form-input
                    type="number"
                    v-model.number="filterYear"
                    min="2020"
                    :max="new Date().getFullYear() + 1"
                    @change="loadYearWise"
                  />
                </b-col>
                <b-col md="3" class="d-flex align-items-end mb-2">
                  <b-button variant="outline-danger" size="sm" @click="clearFilters">
                    <i class="fa fa-times mr-1"></i> Clear
                  </b-button>
                </b-col>
              </b-row>
              <small class="text-muted">
                Add more filters (branch, client, date range) and pass as params to APIs.
              </small>
            </b-card>
          </b-collapse>
        </b-card-header>

        <b-card-body class="p-0">
          <div
            v-if="
              tabChartSeries &&
              tabChartSeries.length &&
              tabChartSeries[0].data &&
              tabChartSeries[0].data.length
            "
            class="px-4 pt-3"
          >
            <apexchart
              type="bar"
              height="220"
              :options="tabChartOptions"
              :series="tabChartSeries"
            ></apexchart>
          </div>
          <b-table
            :items="tableItems"
            :fields="currentFieldsWithSn"
            show-empty
            hover
            head-variant="light"
            sticky-header="600px"
            responsive
            no-border-collapse
            class="mb-0 custom-table"
          >
            <template slot="empty">
              <div class="text-center text-muted my-5">
                <i class="fa fa-inbox fa-3x mb-3"></i>
                <p>No records found.</p>
              </div>
            </template>
            <template slot="SN" slot-scope="row">
              <span>{{
                row.index + 1 + (currentPage - 1) * currentPerPage
              }}</span>
            </template>
            <template slot="clientName" slot-scope="row">
              <b-link
                v-if="currentTabKey === 'client-pending-days' || currentTabKey === 'client-on-acc-aging' || currentTabKey === 'client-due-aging'"
                @click="openClientInvoices(row.item)"
                class="font-weight-bold"
              >
                {{ row.item.clientName }}
              </b-link>
              <span v-else>{{ row.item.clientName }}</span>
            </template>
            <template slot="_expand" slot-scope="row">
              <b-button
                v-if="currentTabKey === 'client-wise-monthly' || currentTabKey === 'client-branch-wise' || currentTabKey === 'agent-wise-monthly' || currentTabKey === 'agent-branch-wise' || currentTabKey === 'shipping-line-wise-monthly' || currentTabKey === 'shipping-line-branch-wise' || currentTabKey === 'branch-wise-monthly' || currentTabKey === 'branch-client-wise' || currentTabKey === 'branch-agent-wise' || currentTabKey === 'branch-shipping-line-wise'"
                variant="link"
                size="sm"
                class="p-0 text-secondary"
                @click.stop="currentTabKey === 'client-wise-monthly'
                  ? toggleClientMonthlyRow(row.item.clientId)
                  : currentTabKey === 'client-branch-wise'
                    ? toggleClientBranchRow(row.item.clientId)
                    : currentTabKey === 'agent-wise-monthly'
                      ? toggleAgentMonthlyRow(row.item.agentId)
                      : currentTabKey === 'agent-branch-wise'
                        ? toggleAgentBranchRow(row.item.agentId)
                        : currentTabKey === 'shipping-line-wise-monthly'
                          ? toggleShippingMonthlyRow(row.item.shippingLineId)
                          : currentTabKey === 'branch-wise-monthly'
                            ? toggleBranchMonthlyRow(row.item.branchId)
                            : currentTabKey === 'branch-client-wise'
                              ? toggleBranchClientRow(row.item.branchId)
                              : currentTabKey === 'branch-agent-wise'
                                ? toggleBranchAgentRow(row.item.branchId)
                                : currentTabKey === 'branch-shipping-line-wise'
                                  ? toggleBranchShippingRow(row.item.branchId)
                                  : toggleShippingBranchRow(row.item.shippingLineId)"
              >
                <i
                  class="fa"
                  :class="expandChevronDown(row) ? 'fa-chevron-down' : 'fa-chevron-right'"
                ></i>
              </b-button>
            </template>
            <template slot="row-details" slot-scope="row">
              <div v-if="(currentTabKey === 'client-wise-monthly' || currentTabKey === 'shipping-line-wise-monthly' || currentTabKey === 'branch-wise-monthly') && row.item.monthlyData" class="bg-light p-3">
                <div v-if="detailMonthlyChartConfig(row.item.monthlyData)" class="mb-3">
                  <apexchart
                    type="line"
                    height="180"
                    :options="detailMonthlyChartConfig(row.item.monthlyData).options"
                    :series="detailMonthlyChartConfig(row.item.monthlyData).series"
                  ></apexchart>
                </div>
                <b-table
                  :items="monthlyDataAsRows(row.item.monthlyData)"
                  :fields="clientMonthlyDetailFields"
                  small
                  striped
                  responsive
                  class="mb-0"
                />
              </div>
              <div v-if="currentTabKey === 'client-branch-wise' && row.item.branch && row.item.branch.length" class="bg-light p-3">
                <div v-if="detailChartConfig('clientBranch', row.item.branch)" class="mb-3">
                  <apexchart
                    type="bar"
                    height="180"
                    :options="detailChartConfig('clientBranch', row.item.branch).options"
                    :series="detailChartConfig('clientBranch', row.item.branch).series"
                  ></apexchart>
                </div>
                <b-table
                  :items="row.item.branch"
                  :fields="clientBranchDetailFields"
                  small
                  striped
                  responsive
                  class="mb-0"
                />
              </div>
              <div v-if="currentTabKey === 'agent-wise-monthly' && row.item.monthlyData" class="bg-light p-3">
                <div v-if="detailMonthlyChartConfig(row.item.monthlyData)" class="mb-3">
                  <apexchart
                    type="line"
                    height="180"
                    :options="detailMonthlyChartConfig(row.item.monthlyData).options"
                    :series="detailMonthlyChartConfig(row.item.monthlyData).series"
                  ></apexchart>
                </div>
                <b-table
                  :items="monthlyDataAsRows(row.item.monthlyData)"
                  :fields="clientMonthlyDetailFields"
                  small
                  striped
                  responsive
                  class="mb-0"
                />
              </div>
              <div v-if="currentTabKey === 'agent-branch-wise' && row.item.branch && row.item.branch.length" class="bg-light p-3">
                <div v-if="detailChartConfig('agentBranch', row.item.branch)" class="mb-3">
                  <apexchart
                    type="bar"
                    height="180"
                    :options="detailChartConfig('agentBranch', row.item.branch).options"
                    :series="detailChartConfig('agentBranch', row.item.branch).series"
                  ></apexchart>
                </div>
                <b-table
                  :items="row.item.branch"
                  :fields="clientBranchDetailFields"
                  small
                  striped
                  responsive
                  class="mb-0"
                />
              </div>
              <div v-if="currentTabKey === 'shipping-line-branch-wise' && row.item.branch && row.item.branch.length" class="bg-light p-3">
                <div v-if="detailChartConfig('shippingBranch', row.item.branch)" class="mb-3">
                  <apexchart
                    type="bar"
                    height="180"
                    :options="detailChartConfig('shippingBranch', row.item.branch).options"
                    :series="detailChartConfig('shippingBranch', row.item.branch).series"
                  ></apexchart>
                </div>
                <b-table
                  :items="row.item.branch"
                  :fields="clientBranchDetailFields"
                  small
                  striped
                  responsive
                  class="mb-0"
                />
              </div>
              <div v-if="currentTabKey === 'branch-client-wise' && row.item.client && row.item.client.length" class="bg-light p-3">
                <div v-if="detailChartConfig('branchClient', row.item.client)" class="mb-3">
                  <apexchart
                    type="bar"
                    height="180"
                    :options="detailChartConfig('branchClient', row.item.client).options"
                    :series="detailChartConfig('branchClient', row.item.client).series"
                  ></apexchart>
                </div>
                <b-table
                  :items="row.item.client"
                  :fields="branchClientDetailFields"
                  small
                  striped
                  responsive
                  class="mb-0"
                />
              </div>
              <div v-if="currentTabKey === 'branch-agent-wise' && row.item.agent && row.item.agent.length" class="bg-light p-3">
                <div v-if="detailChartConfig('branchAgent', row.item.agent)" class="mb-3">
                  <apexchart
                    type="bar"
                    height="180"
                    :options="detailChartConfig('branchAgent', row.item.agent).options"
                    :series="detailChartConfig('branchAgent', row.item.agent).series"
                  ></apexchart>
                </div>
                <b-table
                  :items="row.item.agent"
                  :fields="branchAgentDetailFields"
                  small
                  striped
                  responsive
                  class="mb-0"
                />
              </div>
              <div v-if="currentTabKey === 'branch-shipping-line-wise' && row.item.shippingLine && row.item.shippingLine.length" class="bg-light p-3">
                <div v-if="detailChartConfig('branchShipping', row.item.shippingLine)" class="mb-3">
                  <apexchart
                    type="bar"
                    height="180"
                    :options="detailChartConfig('branchShipping', row.item.shippingLine).options"
                    :series="detailChartConfig('branchShipping', row.item.shippingLine).series"
                  ></apexchart>
                </div>
                <b-table
                  :items="row.item.shippingLine"
                  :fields="branchShippingDetailFields"
                  small
                  striped
                  responsive
                  class="mb-0"
                />
              </div>
            </template>
          </b-table>
        </b-card-body>

        <b-card-footer class="bg-white border-top py-3">
          <div class="d-flex justify-content-between align-items-center">
            <div class="text-muted small">
              Showing
              {{
                currentTotalRows > 0
                  ? (currentPage - 1) * currentPerPage + 1
                  : 0
              }}
              to
              {{ Math.min(currentPage * currentPerPage, currentTotalRows) }}
              of {{ currentTotalRows }} entries
            </div>
            <div class="d-flex align-items-center">
              <b-form-select
                v-model="currentPerPage"
                :options="[10, 25, 50, 100]"
                size="sm"
                class="mr-2"
                style="width: 70px;"
              />
              <b-pagination
                v-model="currentPage"
                :total-rows="currentTotalRows"
                :per-page="currentPerPage"
                align="right"
                class="mb-0 custom-pagination"
                pills
              />
            </div>
          </div>
        </b-card-footer>
      </b-card>
    </section>

    <!-- Import Modal -->
    <b-modal
      v-model="showImportModal"
      title="Import Ledger Outstanding"
      size="xl"
      hide-footer
    >
      <LedgerOutstandingBulkUpload @upload-success="onUploadSuccess" />
    </b-modal>

    <!-- Data Updates Modal -->
    <b-modal
      v-model="showDataUpdatesModal"
      title="Ledger Data Updates"
      size="lg"
      hide-footer
    >
      <template #modal-header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <div class="font-weight-bold">Ledger Data Updates</div>
          <div class="d-flex align-items-center">
            <b-button
              variant="outline-success"
              size="sm"
              class="mr-3"
              @click="exportDataUpdatesExcel"
              :disabled="dataUpdatesLoading || !dataUpdatesRows.length"
            >
              <i class="fa fa-file-excel-o mr-1"></i> Export Excel
            </b-button>
            <button type="button" aria-label="Close" class="close" @click="showDataUpdatesModal = false">×</button>
          </div>
        </div>
      </template>
      <div v-if="dataUpdatesLoading" class="text-center py-4">
        <b-spinner variant="info"></b-spinner>
        <p class="mt-2 text-muted mb-0">Loading data updates...</p>
      </div>
      <div v-else>
        <b-table
          :items="dataUpdatesRows"
          :fields="dataUpdatesFields"
          show-empty
          hover
          head-variant="light"
          responsive
          class="mb-0 custom-table"
        >
          <template slot="cell(created_count)" slot-scope="row">
            {{ formatNumber(row.item.created_count) }}
          </template>
          <template slot="cell(updated_count)" slot-scope="row">
            {{ formatNumber(row.item.updated_count) }}
          </template>
          <template slot="empty">
            <div class="text-center text-muted my-4">
              <p class="mb-0">No update records found.</p>
            </div>
          </template>
        </b-table>
      </div>
    </b-modal>

    <!-- Client Invoices Modal -->
    <b-modal
      v-model="showClientInvoicesModal"
      size="xl"
      hide-footer
      :title="'Invoices - ' + (selectedClient ? selectedClient.clientName : '')"  
    >
      <template #modal-header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <div class="d-flex align-items-center">
            <b-button
              variant="outline-success"
              size="sm"
              class="mr-3"
              @click="exportClientInvoices"
              :disabled="clientInvoicesLoading"
            >
              <i class="fa fa-file-excel-o mr-1"></i> Export Excel
            </b-button>
            <button type="button" aria-label="Close" class="close" @click="showClientInvoicesModal = false">×</button>
          </div>
        </div>
      </template>
      <div v-if="clientInvoicesLoading && !clientInvoices.length" class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2 text-muted">Loading invoices...</p>
      </div>
      <div v-show="clientInvoices.length || !clientInvoicesLoading">
        <div v-if="clientInvoicesLoading && clientInvoices.length > 0" class="text-center mb-2">
          <b-spinner small variant="primary"></b-spinner>
          <span class="ml-2 small text-muted">Refreshing list...</span>
        </div>
        <b-table
          :items="clientInvoices"
          :fields="listFields"
          show-empty
          hover
          head-variant="light"
          sticky-header="450px"
          responsive
          no-border-collapse
          class="mb-3 custom-table"
        >
          <template slot="empty">
            <div class="text-center text-muted my-4">
              <p>No invoices found for this client.</p>
            </div>
          </template>
        </b-table>
        <div class="d-flex justify-content-between align-items-center mt-3">
          <div class="text-muted small">
            Total Records: {{ clientInvoicesPagination.totalCount }}
          </div>
          <b-pagination
            v-model="clientInvoicesPage"
            :total-rows="clientInvoicesPagination.totalCount"
            :per-page="clientInvoicesPerPage"
            align="right"
            class="mb-0"
            pills
            @input="fetchClientInvoices"
          />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import CustomLoader from "../common/customLoader.vue";
import LedgerOutstandingDashboardService from "../../services/LedgerOutstandingDashboardService.js";
import LedgerOutstandingBulkUpload from "./LedgerOutstandingBulkUpload.vue";
import VueApexCharts from "vue-apexcharts";
import * as XLSX from "xlsx";

export default {
  name: "LedgerOutstandingDashboard",
  components: {
    CustomLoader,
    apexchart: VueApexCharts,
    LedgerOutstandingBulkUpload,
  },
  beforeMount() {
    this.$store.dispatch("setCurrentRoute", this.$route.path);
  },
  data() {
    return {
      isLoading: false,
      isRefreshing: false,
      activeTabIndex: 0,
      tabKeys: [
        "base-list",
        "client-pending-days",
        "all-aging",
        "client-due-aging",
        "year-wise",
        "summary",
        "client-wise",
        "client-wise-monthly",
        "client-on-acc-aging",
        "client-branch-wise",
        "agent-wise",
        "agent-wise-monthly",
        "agent-branch-wise",
        "shipping-line-wise",
        "shipping-line-wise-monthly",
        "shipping-line-branch-wise",
        "branch-wise",
        "branch-wise-monthly",
        "branch-client-wise",
        "branch-agent-wise",
        "branch-shipping-line-wise",
        "assignments",
      ],
      allAgingRows: [],
      allAgingPagination: { totalCount: 0 },
      showImportModal: false,
      activeTab: "summary",
      filtersVisible: false,
      filterYear: new Date().getFullYear(),

      listFields: [
        { key: "lom_id", label: "ID" },
        { key: "lom_ledger_name", label: "Ledger Name" },
        { key: "lom_inv_no", label: "Inv No" },
        { key: "lom_doc_date", label: "Doc Date", formatter: (v) => (v ? String(v).slice(0, 10) : "-") },
        { key: "lom_pending_amt", label: "Pending", formatter: (v) => this.formatNumber(v) },
        { key: "lom_pay_amt", label: "Pay Amt", formatter: (v) => this.formatNumber(v) },
        { key: "clientName", label: "Client", formatter: (v, k, item) => (item && item.lom_clientdetail_relation ? item.lom_clientdetail_relation.clientName : "") || "-" },
      ],
      clientMonthlyExpandField: { key: "_expand", label: "", tdClass: "text-nowrap" },
      clientMonthlyFields: [
        // { key: "clientId", label: "Client ID" },
        { key: "clientName", label: "Client Name" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: (v) => this.formatNumber(v) },
        {
          key: "monthlyData",
          label: "Months",
          formatter: (v, k, item) => {
            if (!item.monthlyData || typeof item.monthlyData !== "object") return "—";
            const months = Object.keys(item.monthlyData).sort();
            return months.length ? months.length + " · " + months.join(", ") : "—";
          },
        },
      ],
      clientMonthlyExpandedSet: [],
      clientMonthlyDetailFields: [
        { key: "monthKey", label: "Month" },
        { key: "totalOutstanding", label: "Outstanding", formatter: (v) => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: (v) => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],
      agentMonthlyFields: [
        { key: "agentId", label: "Agent ID" },
        { key: "agentName", label: "Agent Name" },
        { key: "coLoaderName", label: "Co-Loader" },
        { key: "companyName", label: "Company" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: (v) => this.formatNumber(v) },
        {
          key: "monthlyData",
          label: "Months",
          formatter: (v, k, item) => {
            if (!item.monthlyData || typeof item.monthlyData !== "object") return "—";
            const months = Object.keys(item.monthlyData).sort();
            return months.length ? months.length + " · " + months.join(", ") : "—";
          },
        },
      ],
      agentMonthlyExpandedSet: [],
      shippingMonthlyFields: [
        { key: "shippingLineId", label: "Shipping Line ID" },
        { key: "shippingLineName", label: "Shipping Line" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: (v) => this.formatNumber(v) },
        {
          key: "monthlyData",
          label: "Months",
          formatter: (v, k, item) => {
            if (!item.monthlyData || typeof item.monthlyData !== "object") return "—";
            const months = Object.keys(item.monthlyData).sort();
            return months.length ? months.length + " · " + months.join(", ") : "—";
          },
        },
      ],
      shippingMonthlyExpandedSet: [],
      branchMonthlyFields: [
        { key: "branchId", label: "Branch ID" },
        { key: "branchLocation", label: "Branch" },
        { key: "branchMainLocation", label: "Main Location" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: (v) => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: (v) => this.formatNumber(v) },
        {
          key: "monthlyData",
          label: "Months",
          formatter: (v, k, item) => {
            if (!item.monthlyData || typeof item.monthlyData !== "object") return "—";
            const months = Object.keys(item.monthlyData).sort();
            return months.length ? months.length + " · " + months.join(", ") : "—";
          },
        },
      ],
      branchMonthlyExpandedSet: [],

      summary: {
        totalOutstanding: 0,
        totalOutstandingPayAmt: 0,
        totalInvoices: 0,
        totalClients: 0,
        totalAgents: 0,
        totalBranches: 0,
        totalShippingLines: 0,
        totalDueAmount: 0,
        totalNetAmount: 0,
      },

      monthlyChartSeries: [
        {
          name: "Outstanding",
          data: [],
        },
        {
          name: "Pay Amount",
          data: [],
        },
      ],
      monthlyChartOptions: {
        chart: {
          height: 260,
          type: "line",
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 0,
          hover: {
            sizeOffset: 4,
          },
        },
        grid: {
          row: {
            colors: ["#f9fafb", "transparent"],
            opacity: 0.5,
          },
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              var num = parseFloat(val || 0);
              return num.toLocaleString("en-IN");
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              var num = parseFloat(val || 0);
              return num.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
      },

      monthlyFields: [
        { key: "monthKey", label: "Month" },
        { key: "totalOutstanding", label: "Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],

      clientFields: [
        // { key: "clientId", label: "Client ID" },
        { key: "clientName", label: "Client Name" },
        { key: "totalOutstanding", label: "Outstanding", formatter: v => this.formatNumber(v) },
        { key: "due_amount", label: "Due Amount", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],
      agentFields: [
        { key: "agentId", label: "Agent ID" },
        { key: "agentName", label: "Agent Name" },
        { key: "coLoaderName", label: "Co-Loader" },
        { key: "companyName", label: "Company" },
        { key: "totalOutstanding", label: "Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],
      shippingFields: [
        { key: "shippingLineId", label: "Shipping Line ID" },
        { key: "shippingLineName", label: "Shipping Line" },
        { key: "totalOutstanding", label: "Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],
      branchFields: [
        { key: "branchId", label: "Branch ID" },
        { key: "branchLocation", label: "Branch" },
        { key: "branchMainLocation", label: "Main Location" },
        { key: "totalOutstanding", label: "Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],

      clientPendingFields: [
        // { key: "clientId", label: "Client ID" },
        { key: "clientName", label: "Client Name" },
        { key: "net_amount", label: "Net Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
        { key: "creditDays", label: "Credit Days" },
        { key: "on_acc_amt", label: "On Account", formatter: v => this.formatNumber(v) },
        { key: "pending_total", label: "Pending", formatter: v => this.formatNumber(v) },
        { key: "due_amount", label: "Due", formatter: v => this.formatNumber(v) },
        { key: "pending_0_30", label: "0-30 Days", formatter: v => this.formatNumber(v) },
        { key: "pending_31_60", label: "31-60 Days", formatter: v => this.formatNumber(v) },
        { key: "pending_61_90", label: "61-90 Days", formatter: v => this.formatNumber(v) },
        { key: "pending_90_plus", label: "90+ Days", formatter: v => this.formatNumber(v) },
      ],
      allAgingFields: [
        { key: "debtorType", label: "Type", tdClass: "font-weight-bold text-info" },
        { key: "clientName", label: "Name" },
        { key: "net_amount", label: "Net Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
        { key: "creditDays", label: "Credit Days" },
        { key: "on_acc_amt", label: "On Account", formatter: v => this.formatNumber(v) },
        { key: "pending_total", label: "Pending", formatter: v => this.formatNumber(v) },
        { key: "due_amount", label: "Due", formatter: v => this.formatNumber(v) },
        { key: "pending_0_30", label: "0-30 Days", formatter: v => this.formatNumber(v) },
        { key: "pending_31_60", label: "31-60 Days", formatter: v => this.formatNumber(v) },
        { key: "pending_61_90", label: "61-90 Days", formatter: v => this.formatNumber(v) },
        { key: "pending_90_plus", label: "90+ Days", formatter: v => this.formatNumber(v) },
      ],

      clientOnAccFields: [
        { key: "clientName", label: "Client Name" },
        { key: "creditDays", label: "Credit Days" },
        { key: "on_acc_amt", label: "On Account Total", formatter: v => this.formatNumber(v) },
        { key: "on_acc_amt_0_30", label: "On Acc 0-30 Days", formatter: v => this.formatNumber(v) },
        { key: "on_acc_amt_31_60", label: "On Acc 31-60 Days", formatter: v => this.formatNumber(v) },
        { key: "on_acc_amt_61_90", label: "On Acc 61-90 Days", formatter: v => this.formatNumber(v) },
        { key: "on_acc_amt_90_plus", label: "On Acc 90+ Days", formatter: v => this.formatNumber(v) },
      ],

      clientDueFields: [
        { key: "clientName", label: "Client Name" },
        { key: "totalInvoices", label: "Invoices" },
        { key: "creditDays", label: "Credit Days" },
        { key: "due_amount", label: "Due Total", formatter: v => this.formatNumber(v) },
        { key: "due_0_30", label: "Due 0-30 Days", formatter: v => this.formatNumber(v) },
        { key: "due_31_60", label: "Due 31-60 Days", formatter: v => this.formatNumber(v) },
        { key: "due_61_90", label: "Due 61-90 Days", formatter: v => this.formatNumber(v) },
        { key: "due_90_plus", label: "Due 90+ Days", formatter: v => this.formatNumber(v) },
      ],

      clientBranchFields: [
        // { key: "clientId", label: "Client ID" },
        { key: "clientName", label: "Client Name" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Total Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Total Invoices" },
        {
          key: "branch",
          label: "Branches",
          formatter: (v, k, item) => {
            if (!item.branch || !item.branch.length) return "—";
            const locs = item.branch.map((b) => b.branchLocation || b.branchId).filter(Boolean);
            return locs.length ? locs.length + " · " + locs.join(", ") : String(item.branch.length);
          },
        },
      ],
      clientBranchExpandedSet: [],
      clientBranchDetailFields: [
        { key: "branchId", label: "Branch ID" },
        { key: "branchLocation", label: "Branch" },
        { key: "branchMainLocation", label: "Main Location" },
        { key: "totalOutstanding", label: "Outstanding", formatter: (v) => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: (v) => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],

      agentBranchFields: [
        { key: "agentId", label: "Agent ID" },
        { key: "agentName", label: "Agent Name" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Total Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Total Invoices" },
        {
          key: "branch",
          label: "Branches",
          formatter: (v, k, item) => {
            if (!item.branch || !item.branch.length) return "—";
            const locs = item.branch.map((b) => b.branchLocation || b.branchId).filter(Boolean);
            return locs.length ? locs.length + " · " + locs.join(", ") : String(item.branch.length);
          },
        },
      ],
      agentBranchExpandedSet: [],
      shippingBranchFields: [
        { key: "shippingLineId", label: "Shipping Line ID" },
        { key: "shippingLineName", label: "Shipping Line" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Total Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Total Invoices" },
        {
          key: "branch",
          label: "Branches",
          formatter: (v, k, item) => {
            if (!item.branch || !item.branch.length) return "—";
            const locs = item.branch.map((b) => b.branchLocation || b.branchId).filter(Boolean);
            return locs.length ? locs.length + " · " + locs.join(", ") : String(item.branch.length);
          },
        },
      ],
      shippingBranchExpandedSet: [],

      branchClientFields: [
        { key: "branchId", label: "Branch ID" },
        { key: "branchLocation", label: "Branch" },
        { key: "branchMainLocation", label: "Main Location" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Total Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Total Invoices" },
        {
          key: "client",
          label: "Clients",
          formatter: (v, k, item) => {
            if (!item.client || !item.client.length) return "—";
            const names = item.client.map((c) => c.clientName || c.clientId).filter(Boolean);
            return names.length ? names.length + " · " + names.slice(0, 3).join(", ") + (names.length > 3 ? "…" : "") : String(item.client.length);
          },
        },
      ],
      branchClientExpandedSet: [],
      branchClientDetailFields: [
        // { key: "clientId", label: "Client ID" },
        { key: "clientName", label: "Client Name" },
        { key: "totalOutstanding", label: "Outstanding", formatter: (v) => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: (v) => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],

      branchAgentFields: [
        { key: "branchId", label: "Branch ID" },
        { key: "branchLocation", label: "Branch" },
        { key: "branchMainLocation", label: "Main Location" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Total Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Total Invoices" },
        {
          key: "agent",
          label: "Agents",
          formatter: (v, k, item) => {
            if (!item.agent || !item.agent.length) return "—";
            const names = item.agent.map((a) => a.agentName || a.agentId).filter(Boolean);
            return names.length ? names.length + " · " + names.slice(0, 3).join(", ") + (names.length > 3 ? "…" : "") : String(item.agent.length);
          },
        },
      ],
      branchAgentExpandedSet: [],
      branchAgentDetailFields: [
        { key: "agentId", label: "Agent ID" },
        { key: "agentName", label: "Agent Name" },
        { key: "coLoaderName", label: "Co-Loader" },
        { key: "companyName", label: "Company" },
        { key: "totalOutstanding", label: "Outstanding", formatter: (v) => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: (v) => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],

      branchShippingFields: [
        { key: "branchId", label: "Branch ID" },
        { key: "branchLocation", label: "Branch" },
        { key: "branchMainLocation", label: "Main Location" },
        { key: "totalOutstanding", label: "Total Outstanding", formatter: v => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Total Pay Amount", formatter: v => this.formatNumber(v) },
        { key: "totalInvoices", label: "Total Invoices" },
        {
          key: "shippingLine",
          label: "Shipping Lines",
          formatter: (v, k, item) => {
            if (!item.shippingLine || !item.shippingLine.length) return "—";
            const names = item.shippingLine.map((s) => s.shippingLineName || s.shippingLineId).filter(Boolean);
            return names.length ? names.length + " · " + names.slice(0, 3).join(", ") + (names.length > 3 ? "…" : "") : String(item.shippingLine.length);
          },
        },
      ],
      branchShippingExpandedSet: [],
      branchShippingDetailFields: [
        { key: "shippingLineId", label: "Shipping Line ID" },
        { key: "shippingLineName", label: "Shipping Line" },
        { key: "totalOutstanding", label: "Outstanding", formatter: (v) => this.formatNumber(v) },
        { key: "totalOutstandingPayAmt", label: "Pay Amount", formatter: (v) => this.formatNumber(v) },
        { key: "totalInvoices", label: "Invoices" },
      ],

      assignmentsFields: [
        { key: "jobNo", label: "Job No" },
        { key: "clientName", label: "Client" },
        { key: "branchName", label: "Branch" },
        { key: "moduleType", label: "Module" },
        { key: "pendingAmount", label: "Pending", formatter: v => this.formatNumber(v) },
      ],

      monthlyRows: [],
      monthlyPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      listRows: [],
      listPagination: { currentPage: 1, pageSize: 10, totalCount: 0, totalPages: 0 },
      yearWiseRows: [],
      yearWisePagination: { currentPage: 1, pageSize: 10, totalCount: 0, totalPages: 0 },
      clientRows: [],
      clientPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      agentRows: [],
      agentPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      shippingRows: [],
      shippingPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      branchRows: [],
      branchPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      clientMonthlyRows: [],
      clientMonthlyPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      agentMonthlyRows: [],
      agentMonthlyPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      shippingMonthlyRows: [],
      shippingMonthlyPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      branchMonthlyRows: [],
      branchMonthlyPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },

      clientPendingRows: [],
      clientPendingPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      clientBranchRows: [],
      clientBranchPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      agentBranchRows: [],
      agentBranchPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      shippingBranchRows: [],
      shippingBranchPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      branchClientRows: [],
      branchClientPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      branchAgentRows: [],
      branchAgentPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      branchShippingRows: [],
      branchShippingPagination: { currentPage: 1, pageSize: 25, totalCount: 0, totalPages: 0 },
      assignmentsRows: [],
      assignmentsPagination: { currentPage: 1, pageSize: 10, totalCount: 0, totalPages: 0 },

      clientSearch: "",
      agentSearch: "",
      shippingSearch: "",
      branchSearch: "",

      clientPendingSearch: "",
      clientBranchSearch: "",
      agentBranchSearch: "",
      shippingBranchSearch: "",
      branchClientSearch: "",
      branchAgentSearch: "",
      branchShippingSearch: "",
      allAgingSearch: "",
      assignmentsSearch: "",

      listPage: 1,
      listPerPage: 10,
      yearWisePage: 1,
      yearWisePerPage: 10,
      clientPage: 1,
      agentPage: 1,
      shippingPage: 1,
      branchPage: 1,
      clientMonthlyPage: 1,
      agentMonthlyPage: 1,
      shippingMonthlyPage: 1,
      branchMonthlyPage: 1,

      clientPendingPage: 1,
      allAgingPage: 1,
      clientBranchPage: 1,
      agentBranchPage: 1,
      shippingBranchPage: 1,
      branchClientPage: 1,
      branchAgentPage: 1,
      branchShippingPage: 1,
      assignmentsPage: 1,
      assignmentsPerPage: 10,

      monthlyPage: 1,

      clientPerPage: 25,
      agentPerPage: 25,
      shippingPerPage: 25,
      branchPerPage: 25,
      clientMonthlyPerPage: 25,
      agentMonthlyPerPage: 25,
      shippingMonthlyPerPage: 25,
      branchMonthlyPerPage: 25,
      clientPendingPerPage: 25,
      allAgingPerPage: 25,
      clientBranchPerPage: 25,
      agentBranchPerPage: 25,
      shippingBranchPerPage: 25,
      branchClientPerPage: 25,
      branchAgentPerPage: 25,
      branchShippingPerPage: 25,
      branchShippingPerPage: 25,
      monthlyPerPage: 12,
      globalSearch: "",

      // Client Invoices Modal
      showClientInvoicesModal: false,
      clientInvoices: [],
      clientInvoicesLoading: false,
      selectedClient: null,
      clientInvoicesPage: 1,
      clientInvoicesPerPage: 10,
      clientInvoicesPagination: { totalCount: 0 },

      // Data Updates Modal
      showDataUpdatesModal: false,
      dataUpdatesLoading: false,
      dataUpdatesRows: [],
      dataUpdatesFields: [
        { key: "date", label: "Date", formatter: (v) => this.formatUpdateDate(v) },
        { key: "day_name", label: "Day" },
        { key: "created_count", label: "Created Count", class: "text-right" },
        { key: "updated_count", label: "Updated Count", class: "text-right" },
      ],
    };
  },
  created() {
    this.loadAllData();
  },
  computed: {
    currentTabKey() {
      return this.tabKeys[this.activeTabIndex] || "summary";
    },
    totalEntityCount() {
      const s = this.summary || {};
      const c = Number(s.totalClients || 0);
      const a = Number(s.totalAgents || 0);
      const b = Number(s.totalBranches || 0);
      const sh = Number(s.totalShippingLines || 0);
      return c + a + b + sh;
    },
    normalizedMonthlyBars() {
      const rows = this.monthlyRows || [];
      if (!rows.length) return [];

      // Use absolute values so months with negative totals still show bars
      const valuesAbs = rows.map((r) => Math.abs(Number(r.totalOutstanding || 0)));
      const maxAbs = Math.max.apply(null, valuesAbs.concat(0));
      if (!maxAbs) {
        return rows.map((r) => ({
          monthKey: r.monthKey,
          height: 0,
        }));
      }
      return rows.map((r) => {
        const val = Math.abs(Number(r.totalOutstanding || 0));
        let height = Math.round((val / maxAbs) * 100);
        if (height < 5 && val > 0) height = 5;
        return {
          monthKey: r.monthKey,
          height,
        };
      });
    },
    currentFields() {
      const k = this.currentTabKey;
      const map = {
        "base-list": this.listFields,
        "year-wise": this.listFields,
        summary: this.monthlyFields,
        "client-wise": this.clientFields,
        "client-wise-monthly": [this.clientMonthlyExpandField, ...this.clientMonthlyFields],
        "client-pending-days": this.clientPendingFields,
        "all-aging": this.allAgingFields,
        "client-on-acc-aging": this.clientOnAccFields,
        "client-due-aging": this.clientDueFields,
        "client-branch-wise": [this.clientMonthlyExpandField, ...this.clientBranchFields],
        "agent-wise": this.agentFields,
        "agent-wise-monthly": [this.clientMonthlyExpandField, ...this.agentMonthlyFields],
        "agent-branch-wise": [this.clientMonthlyExpandField, ...this.agentBranchFields],
        "shipping-line-wise": this.shippingFields,
        "shipping-line-wise-monthly": [this.clientMonthlyExpandField].concat(this.shippingMonthlyFields),
        "shipping-line-branch-wise": [this.clientMonthlyExpandField, ...this.shippingBranchFields],
        "branch-wise": this.branchFields,
        "branch-wise-monthly": [this.clientMonthlyExpandField].concat(this.branchMonthlyFields),
        "branch-client-wise": [this.clientMonthlyExpandField].concat(this.branchClientFields),
        "branch-agent-wise": [this.clientMonthlyExpandField].concat(this.branchAgentFields),
        "branch-shipping-line-wise": [this.clientMonthlyExpandField].concat(this.branchShippingFields),
        assignments: this.listFields,
      };
      return map[k] || this.monthlyFields;
    },
    tabChartDefinition() {
      const k = this.currentTabKey;
      const rows = this.currentPagedItems || [];
      if (!rows || !rows.length) return null;

      const cfgMap = {
        "base-list": { xKey: "lom_doc_date", outKey: "lom_pending_amt", payKey: "lom_pay_amt", title: "Pending by Document Date" },
        "client-wise": { xKey: "clientName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Client" },
        "client-wise-monthly": { xKey: "clientName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Client" },
        "client-pending-days": { xKey: "clientName", outKey: "pending_total", payKey: null, title: "Total Pending by Client" },
        "all-aging": { xKey: "clientName", outKey: "pending_total", payKey: null, title: "Total Pending (Consolidated)" },
        "client-on-acc-aging": { xKey: "clientName", outKey: "on_acc_amt", payKey: null, title: "On Account by Client" },
        "client-due-aging": { xKey: "clientName", outKey: "due_amount", payKey: null, title: "Due by Client" },
        "client-branch-wise": { xKey: "clientName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Client" },
        "agent-wise": { xKey: "agentName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Agent" },
        "agent-wise-monthly": { xKey: "agentName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Agent" },
        "agent-branch-wise": { xKey: "agentName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Agent" },
        "shipping-line-wise": { xKey: "shippingLineName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Shipping Line" },
        "shipping-line-wise-monthly": { xKey: "shippingLineName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Shipping Line" },
        "shipping-line-branch-wise": { xKey: "shippingLineName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Shipping Line" },
        "branch-wise": { xKey: "branchLocation", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Branch" },
        "branch-wise-monthly": { xKey: "branchLocation", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Branch" },
        "branch-client-wise": { xKey: "branchLocation", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Branch" },
        "branch-agent-wise": { xKey: "branchLocation", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Branch" },
        "branch-shipping-line-wise": { xKey: "branchLocation", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Branch" },
      };

      const cfg = cfgMap[k];
      if (!cfg) return null;

      const categories = [];
      const outData = [];
      const payData = [];

      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const xVal = r[cfg.xKey];
        if (xVal == null || xVal === "") continue;
        categories.push(String(xVal));
        outData.push(Number(r[cfg.outKey] || 0));
        if (cfg.payKey) {
          payData.push(Number(r[cfg.payKey] || 0));
        }
      }

      if (!categories.length) return null;

      const series = [
        { name: "Outstanding", data: outData },
      ];
      if (cfg.payKey) {
        series.push({ name: "Pay Amount", data: payData });
      }

      return {
        title: cfg.title,
        categories,
        series,
      };
    },
    tabChartSeries() {
      const def = this.tabChartDefinition;
      if (!def) return [];
      return def.series || [];
    },
    tabChartOptions() {
      const def = this.tabChartDefinition;
      const base = {
        chart: {
          height: 220,
          type: "bar",
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: {
            columnWidth: "55%",
          },
        },
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              var num = parseFloat(val || 0);
              return num.toLocaleString("en-IN");
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              var num = parseFloat(val || 0);
              return num.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
        title: {
          text: "",
          align: "left",
        },
      };

      if (!def) return base;
      base.xaxis.categories = def.categories || [];
      base.title.text = def.title || "";
      return base;
    },
    currentFieldsWithSn() {
      return [{ key: "SN", label: "SN" }, ...this.currentFields];
    },
    tableItems() {
      const items = this.currentPagedItems;
      const k = this.currentTabKey;
      if (k === "client-wise-monthly") {
        const set = this.clientMonthlyExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.clientId) >= 0;
          return row;
        });
      }
      if (k === "client-branch-wise") {
        const set = this.clientBranchExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.clientId) >= 0;
          return row;
        });
      }
      if (k === "agent-wise-monthly") {
        const set = this.agentMonthlyExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.agentId) >= 0;
          return row;
        });
      }
      if (k === "agent-branch-wise") {
        const set = this.agentBranchExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.agentId) >= 0;
          return row;
        });
      }
      if (k === "shipping-line-wise-monthly") {
        const set = this.shippingMonthlyExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.shippingLineId) >= 0;
          return row;
        });
      }
      if (k === "branch-wise-monthly") {
        const set = this.branchMonthlyExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.branchId) >= 0;
          return row;
        });
      }
      if (k === "branch-client-wise") {
        const set = this.branchClientExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.branchId) >= 0;
          return row;
        });
      }
      if (k === "branch-agent-wise") {
        const set = this.branchAgentExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.branchId) >= 0;
          return row;
        });
      }
      if (k === "branch-shipping-line-wise") {
        const set = this.branchShippingExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.branchId) >= 0;
          return row;
        });
      }
      if (k === "shipping-line-branch-wise") {
        const set = this.shippingBranchExpandedSet || [];
        return items.map(function (item) {
          const row = Object.assign({}, item);
          row._showDetails = set.indexOf(item.shippingLineId) >= 0;
          return row;
        });
      }
      return items;
    },
    currentFilteredItems() {
      const k = this.currentTabKey;
      const backendPaginated = {
        "base-list": () => this.listRows,
        "year-wise": () => this.yearWiseRows,
        assignments: () => this.assignmentsRows,
        summary: () => this.monthlyRows,
        "client-wise": () => this.clientRows,
        "client-wise-monthly": () => this.clientMonthlyRows,
        "client-pending-days": () => this.clientPendingRows,
        "client-on-acc-aging": () => this.clientPendingRows,
        "client-due-aging": () => this.clientPendingRows,
        "client-branch-wise": () => this.clientBranchRows,
        "agent-wise": () => this.agentRows,
        "agent-wise-monthly": () => this.agentMonthlyRows,
        "agent-branch-wise": () => this.agentBranchRows,
        "shipping-line-wise": () => this.shippingRows,
        "shipping-line-wise-monthly": () => this.shippingMonthlyRows,
        "shipping-line-branch-wise": () => this.shippingBranchRows,
        "branch-wise": () => this.branchRows,
        "branch-wise-monthly": () => this.branchMonthlyRows,
        "branch-client-wise": () => this.branchClientRows,
        "branch-agent-wise": () => this.branchAgentRows,
        "branch-shipping-line-wise": () => this.branchShippingRows,
        "all-aging": () => this.allAgingRows,
      };
      if (backendPaginated[k]) return backendPaginated[k]();
      const q = (this.globalSearch || "").toLowerCase();
      let list = [];
      const lists = {
        "client-wise": this.filteredClientItems,
        "client-pending-days": this.filteredClientPendingItems,
        "client-branch-wise": this.filteredClientBranchItems,
        "agent-wise": this.filteredAgentItems,
        "agent-branch-wise": this.filteredAgentBranchItems,
        "shipping-line-wise": this.filteredShippingItems,
        "shipping-line-branch-wise": this.filteredShippingBranchItems,
        "branch-wise": this.filteredBranchItems,
        "branch-client-wise": this.filteredBranchClientItems,
        "branch-agent-wise": this.filteredBranchAgentItems,
        "branch-shipping-line-wise": this.filteredBranchShippingItems,
      };
      list = lists[k] || [];
      if (q && list.length) {
        list = list.filter((r) =>
          JSON.stringify(r).toLowerCase().includes(q)
        );
      }
      return list;
    },
    currentTotalRows() {
      const k = this.currentTabKey;
      const paginationMap = {
        "base-list": this.listPagination,
        "year-wise": this.yearWisePagination,
        assignments: this.assignmentsPagination,
        summary: this.monthlyPagination,
        "client-wise": this.clientPagination,
        "client-wise-monthly": this.clientMonthlyPagination,
        "client-pending-days": this.clientPendingPagination,
        "client-on-acc-aging": this.clientPendingPagination,
        "client-due-aging": this.clientPendingPagination,
        "client-branch-wise": this.clientBranchPagination,
        "agent-wise": this.agentPagination,
        "agent-wise-monthly": this.agentMonthlyPagination,
        "agent-branch-wise": this.agentBranchPagination,
        "shipping-line-wise": this.shippingPagination,
        "shipping-line-wise-monthly": this.shippingMonthlyPagination,
        "shipping-line-branch-wise": this.shippingBranchPagination,
        "branch-wise": this.branchPagination,
        "branch-wise-monthly": this.branchMonthlyPagination,
        "branch-client-wise": this.branchClientPagination,
        "branch-agent-wise": this.branchAgentPagination,
        "branch-shipping-line-wise": this.branchShippingPagination,
        "all-aging": this.allAgingPagination,
      };
      const pag = paginationMap[k];
      if (pag) return pag.totalCount || 0;
      return this.currentFilteredItems.length;
    },
    currentPagedItems() {
      const k = this.currentTabKey;
      const backendPaginated = {
        "base-list": () => this.listRows,
        "year-wise": () => this.yearWiseRows,
        assignments: () => this.assignmentsRows,
        summary: () => this.monthlyRows,
        "client-wise": () => this.clientRows,
        "client-wise-monthly": () => this.clientMonthlyRows,
        "client-pending-days": () => this.clientPendingRows,
        "client-on-acc-aging": () => this.clientPendingRows,
        "client-due-aging": () => this.clientPendingRows,
        "client-branch-wise": () => this.clientBranchRows,
        "agent-wise": () => this.agentRows,
        "agent-wise-monthly": () => this.agentMonthlyRows,
        "agent-branch-wise": () => this.agentBranchRows,
        "shipping-line-wise": () => this.shippingRows,
        "shipping-line-wise-monthly": () => this.shippingMonthlyRows,
        "shipping-line-branch-wise": () => this.shippingBranchRows,
        "branch-wise": () => this.branchRows,
        "branch-wise-monthly": () => this.branchMonthlyRows,
        "branch-client-wise": () => this.branchClientRows,
        "branch-agent-wise": () => this.branchAgentRows,
        "branch-shipping-line-wise": () => this.branchShippingRows,
        "all-aging": () => this.allAgingRows,
      };
      if (backendPaginated[k]) return backendPaginated[k]();
      const start = (this.currentPage - 1) * this.currentPerPage;
      return this.currentFilteredItems.slice(
        start,
        start + this.currentPerPage
      );
    },
    currentPage: {
      get() {
        const k = this.currentTabKey;
        const pages = {
          "base-list": this.listPage,
          "year-wise": this.yearWisePage,
          summary: this.monthlyPage,
          "client-wise": this.clientPage,
          "client-wise-monthly": this.clientMonthlyPage,
          "client-pending-days": this.clientPendingPage,
          "client-on-acc-aging": this.clientPendingPage,
          "client-due-aging": this.clientPendingPage,
          "client-branch-wise": this.clientBranchPage,
          "agent-wise": this.agentPage,
          "agent-wise-monthly": this.agentMonthlyPage,
          "agent-branch-wise": this.agentBranchPage,
          "shipping-line-wise": this.shippingPage,
          "shipping-line-wise-monthly": this.shippingMonthlyPage,
          "shipping-line-branch-wise": this.shippingBranchPage,
          "branch-wise": this.branchPage,
          "branch-wise-monthly": this.branchMonthlyPage,
          "branch-client-wise": this.branchClientPage,
          "branch-agent-wise": this.branchAgentPage,
          "branch-shipping-line-wise": this.branchShippingPage,
          "all-aging": this.allAgingPage,
          assignments: this.assignmentsPage,
        };
        return (pages[k] != null ? pages[k] : 1);
      },
      set(v) {
        const k = this.currentTabKey;
        const setters = {
          "base-list": (x) => (this.listPage = x),
          "year-wise": (x) => (this.yearWisePage = x),
          summary: (x) => (this.monthlyPage = x),
          "client-wise": (x) => (this.clientPage = x),
          "client-wise-monthly": (x) => (this.clientMonthlyPage = x),
          "client-pending-days": (x) => (this.clientPendingPage = x),
          "client-on-acc-aging": (x) => (this.clientPendingPage = x),
          "client-due-aging": (x) => (this.clientPendingPage = x),
          "client-branch-wise": (x) => (this.clientBranchPage = x),
          "agent-wise": (x) => (this.agentPage = x),
          "agent-wise-monthly": (x) => (this.agentMonthlyPage = x),
          "agent-branch-wise": (x) => (this.agentBranchPage = x),
          "shipping-line-wise": (x) => (this.shippingPage = x),
          "shipping-line-wise-monthly": (x) => (this.shippingMonthlyPage = x),
          "shipping-line-branch-wise": (x) => (this.shippingBranchPage = x),
          "branch-wise": (x) => (this.branchPage = x),
          "branch-wise-monthly": (x) => (this.branchMonthlyPage = x),
          "branch-client-wise": (x) => (this.branchClientPage = x),
          "branch-agent-wise": (x) => (this.branchAgentPage = x),
          "branch-shipping-line-wise": (x) => (this.branchShippingPage = x),
          "all-aging": (x) => (this.allAgingPage = x),
          assignments: (x) => (this.assignmentsPage = x),
        };
        if (setters[k]) setters[k](v);
        const loaders = {
          "base-list": () => this.loadList(),
          "year-wise": () => this.loadYearWise(),
          assignments: () => this.loadAssignments(),
          summary: () => this.loadMonthly(),
          "client-wise": () => this.loadClientWise(),
          "client-wise-monthly": () => this.loadClientWiseMonthly(),
          "client-pending-days": () => this.loadClientPendingDays(),
          "client-on-acc-aging": () => this.loadClientPendingDays(),
          "client-due-aging": () => this.loadClientPendingDays(),
          "client-branch-wise": () => this.loadClientBranchWise(),
          "agent-wise": () => this.loadAgentWise(),
          "agent-wise-monthly": () => this.loadAgentWiseMonthly(),
          "agent-branch-wise": () => this.loadAgentBranchWise(),
          "shipping-line-wise": () => this.loadShippingWise(),
          "shipping-line-wise-monthly": () => this.loadShippingLineWiseMonthly(),
          "shipping-line-branch-wise": () => this.loadShippingBranchWise(),
          "branch-wise": () => this.loadBranchWise(),
          "branch-wise-monthly": () => this.loadBranchWiseMonthly(),
          "branch-client-wise": () => this.loadBranchClientWise(),
          "branch-agent-wise": () => this.loadBranchAgentWise(),
          "branch-shipping-line-wise": () => this.loadBranchShippingWise(),
          "all-aging": () => this.loadAllAging(),
        };
        if (loaders[k]) loaders[k]();
      },
    },
    currentPerPage: {
      get() {
        const k = this.currentTabKey;
        const map = {
          "base-list": this.listPerPage,
          "year-wise": this.yearWisePerPage,
          summary: this.monthlyPerPage,
          "client-wise": this.clientPerPage,
          "client-wise-monthly": this.clientMonthlyPerPage,
          "client-pending-days": this.clientPendingPerPage,
          "client-on-acc-aging": this.clientPendingPerPage,
          "client-due-aging": this.clientPendingPerPage,
          "client-branch-wise": this.clientBranchPerPage,
          "agent-wise": this.agentPerPage,
          "agent-wise-monthly": this.agentMonthlyPerPage,
          "agent-branch-wise": this.agentBranchPerPage,
          "shipping-line-wise": this.shippingPerPage,
          "shipping-line-wise-monthly": this.shippingMonthlyPerPage,
          "shipping-line-branch-wise": this.shippingBranchPerPage,
          "branch-wise": this.branchPerPage,
          "branch-wise-monthly": this.branchMonthlyPerPage,
          "branch-client-wise": this.branchClientPerPage,
          "branch-agent-wise": this.branchAgentPerPage,
          "branch-shipping-line-wise": this.branchShippingPerPage,
          "all-aging": this.allAgingPerPage,
          assignments: this.assignmentsPerPage,
        };
        return (map[k] != null ? map[k] : 25);
      },
      set(v) {
        const k = this.currentTabKey;
        const setters = {
          "base-list": (x) => (this.listPerPage = x),
          "year-wise": (x) => (this.yearWisePerPage = x),
          summary: (x) => (this.monthlyPerPage = x),
          "client-wise": (x) => (this.clientPerPage = x),
          "client-wise-monthly": (x) => (this.clientMonthlyPerPage = x),
          "client-pending-days": (x) => (this.clientPendingPerPage = x),
          "client-on-acc-aging": (x) => (this.clientPendingPerPage = x),
          "client-due-aging": (x) => (this.clientPendingPerPage = x),
          "client-branch-wise": (x) => (this.clientBranchPerPage = x),
          "agent-wise": (x) => (this.agentPerPage = x),
          "agent-wise-monthly": (x) => (this.agentMonthlyPerPage = x),
          "agent-branch-wise": (x) => (this.agentBranchPerPage = x),
          "shipping-line-wise": (x) => (this.shippingPerPage = x),
          "shipping-line-wise-monthly": (x) => (this.shippingMonthlyPerPage = x),
          "shipping-line-branch-wise": (x) => (this.shippingBranchPerPage = x),
          "branch-wise": (x) => (this.branchPerPage = x),
          "branch-wise-monthly": (x) => (this.branchMonthlyPerPage = x),
          "branch-client-wise": (x) => (this.branchClientPerPage = x),
          "branch-agent-wise": (x) => (this.branchAgentPerPage = x),
          "branch-shipping-line-wise": (x) =>
            (this.branchShippingPerPage = x),
          "all-aging": (x) => (this.allAgingPerPage = x),
          assignments: (x) => (this.assignmentsPerPage = x),
        };
        if (setters[k]) setters[k](v);
        const loaders = {
          "base-list": () => this.loadList(),
          "year-wise": () => this.loadYearWise(),
          assignments: () => this.loadAssignments(),
          summary: () => this.loadMonthly(),
          "client-wise": () => this.loadClientWise(),
          "client-wise-monthly": () => this.loadClientWiseMonthly(),
          "client-pending-days": () => this.loadClientPendingDays(),
          "client-on-acc-aging": () => this.loadClientPendingDays(),
          "client-due-aging": () => this.loadClientPendingDays(),
          "client-branch-wise": () => this.loadClientBranchWise(),
          "agent-wise": () => this.loadAgentWise(),
          "agent-wise-monthly": () => this.loadAgentWiseMonthly(),
          "agent-branch-wise": () => this.loadAgentBranchWise(),
          "shipping-line-wise": () => this.loadShippingWise(),
          "shipping-line-wise-monthly": () => this.loadShippingLineWiseMonthly(),
          "shipping-line-branch-wise": () => this.loadShippingBranchWise(),
          "branch-wise": () => this.loadBranchWise(),
          "branch-wise-monthly": () => this.loadBranchWiseMonthly(),
          "branch-client-wise": () => this.loadBranchClientWise(),
          "branch-agent-wise": () => this.loadBranchAgentWise(),
          "branch-shipping-line-wise": () => this.loadBranchShippingWise(),
          "all-aging": () => this.loadAllAging(),
        };
        if (loaders[k]) loaders[k]();
      },
    },
    pagedMonthlyItems() {
      const start = (this.monthlyPage - 1) * this.monthlyPerPage;
      return this.monthlyRows.slice(start, start + this.monthlyPerPage);
    },

    filteredClientItems() {
      const q = this.clientSearch.toLowerCase();
      if (!q) return this.clientRows;
      return this.clientRows.filter(
        (r) =>
          String(r.clientId).includes(q) ||
          (r.clientName || "").toLowerCase().includes(q),
      );
    },
    pagedClientItems() {
      const start = (this.clientPage - 1) * this.clientPerPage;
      return this.filteredClientItems.slice(
        start,
        start + this.clientPerPage,
      );
    },

    filteredAgentItems() {
      const q = this.agentSearch.toLowerCase();
      if (!q) return this.agentRows;
      return this.agentRows.filter(
        (r) =>
          String(r.agentId).includes(q) ||
          (r.agentName || "").toLowerCase().includes(q) ||
          (r.companyName || "").toLowerCase().includes(q),
      );
    },
    pagedAgentItems() {
      const start = (this.agentPage - 1) * this.agentPerPage;
      return this.filteredAgentItems.slice(
        start,
        start + this.agentPerPage,
      );
    },

    filteredShippingItems() {
      const q = this.shippingSearch.toLowerCase();
      if (!q) return this.shippingRows;
      return this.shippingRows.filter(
        (r) =>
          String(r.shippingLineId).includes(q) ||
          (r.shippingLineName || "").toLowerCase().includes(q),
      );
    },
    pagedShippingItems() {
      const start = (this.shippingPage - 1) * this.shippingPerPage;
      return this.filteredShippingItems.slice(
        start,
        start + this.shippingPerPage,
      );
    },

    filteredBranchItems() {
      const q = this.branchSearch.toLowerCase();
      if (!q) return this.branchRows;
      return this.branchRows.filter(
        (r) =>
          String(r.branchId).includes(q) ||
          (r.branchLocation || "").toLowerCase().includes(q) ||
          (r.branchMainLocation || "").toLowerCase().includes(q),
      );
    },
    pagedBranchItems() {
      const start = (this.branchPage - 1) * this.branchPerPage;
      return this.filteredBranchItems.slice(
        start,
        start + this.branchPerPage,
      );
    },

    filteredClientPendingItems() {
      const q = this.clientPendingSearch.toLowerCase();
      if (!q) return this.clientPendingRows;
      return this.clientPendingRows.filter(
        (r) =>
          String(r.clientId).includes(q) ||
          (r.clientName || "").toLowerCase().includes(q),
      );
    },
    pagedClientPendingItems() {
      const start =
        (this.clientPendingPage - 1) * this.clientPendingPerPage;
      return this.filteredClientPendingItems.slice(
        start,
        start + this.clientPendingPerPage,
      );
    },

    filteredClientBranchItems() {
      const q = this.clientBranchSearch.toLowerCase();
      if (!q) return this.clientBranchRows;
      return this.clientBranchRows.filter(
        (r) =>
          String(r.clientId).includes(q) ||
          (r.clientName || "").toLowerCase().includes(q),
      );
    },
    pagedClientBranchItems() {
      const start =
        (this.clientBranchPage - 1) * this.clientBranchPerPage;
      return this.filteredClientBranchItems.slice(
        start,
        start + this.clientBranchPerPage,
      );
    },

    filteredAgentBranchItems() {
      const q = this.agentBranchSearch.toLowerCase();
      if (!q) return this.agentBranchRows;
      return this.agentBranchRows.filter(
        (r) =>
          String(r.agentId).includes(q) ||
          (r.agentName || "").toLowerCase().includes(q),
      );
    },
    pagedAgentBranchItems() {
      const start =
        (this.agentBranchPage - 1) * this.agentBranchPerPage;
      return this.filteredAgentBranchItems.slice(
        start,
        start + this.agentBranchPerPage,
      );
    },

    filteredShippingBranchItems() {
      const q = this.shippingBranchSearch.toLowerCase();
      if (!q) return this.shippingBranchRows;
      return this.shippingBranchRows.filter(
        (r) =>
          String(r.shippingLineId).includes(q) ||
          (r.shippingLineName || "").toLowerCase().includes(q),
      );
    },
    pagedShippingBranchItems() {
      const start =
        (this.shippingBranchPage - 1) * this.shippingBranchPerPage;
      return this.filteredShippingBranchItems.slice(
        start,
        start + this.shippingBranchPerPage,
      );
    },

    filteredBranchClientItems() {
      const q = this.branchClientSearch.toLowerCase();
      if (!q) return this.branchClientRows;
      return this.branchClientRows.filter(
        (r) =>
          String(r.branchId).includes(q) ||
          (r.branchLocation || "").toLowerCase().includes(q) ||
          (r.branchMainLocation || "").toLowerCase().includes(q),
      );
    },
    pagedBranchClientItems() {
      const start =
        (this.branchClientPage - 1) * this.branchClientPerPage;
      return this.filteredBranchClientItems.slice(
        start,
        start + this.branchClientPerPage,
      );
    },

    filteredBranchAgentItems() {
      const q = this.branchAgentSearch.toLowerCase();
      if (!q) return this.branchAgentRows;
      return this.branchAgentRows.filter(
        (r) =>
          String(r.branchId).includes(q) ||
          (r.branchLocation || "").toLowerCase().includes(q) ||
          (r.branchMainLocation || "").toLowerCase().includes(q),
      );
    },
    pagedBranchAgentItems() {
      const start =
        (this.branchAgentPage - 1) * this.branchAgentPerPage;
      return this.filteredBranchAgentItems.slice(
        start,
        start + this.branchAgentPerPage,
      );
    },

    filteredBranchShippingItems() {
      const q = this.branchShippingSearch.toLowerCase();
      if (!q) return this.branchShippingRows;
      return this.branchShippingRows.filter(
        (r) =>
          String(r.branchId).includes(q) ||
          (r.branchLocation || "").toLowerCase().includes(q) ||
          (r.branchMainLocation || "").toLowerCase().includes(q),
      );
    },
    pagedBranchShippingItems() {
      const start =
        (this.branchShippingPage - 1) * this.branchShippingPerPage;
      return this.filteredBranchShippingItems.slice(
        start,
        start + this.branchShippingPerPage,
      );
    },

    filteredAssignmentsItems() {
      const q = this.assignmentsSearch.toLowerCase();
      if (!q) return this.assignmentsRows;
      return this.assignmentsRows.filter(
        (r) =>
          String(r.jobNo || "").toLowerCase().includes(q) ||
          (r.clientName || "").toLowerCase().includes(q) ||
          (r.branchName || "").toLowerCase().includes(q),
      );
    },
    pagedAssignmentsItems() {
      const start =
        (this.assignmentsPage - 1) * this.assignmentsPerPage;
      return this.filteredAssignmentsItems.slice(
        start,
        start + this.assignmentsPerPage,
      );
    },
  },
  methods: {
    openDataUpdatesModal() {
      this.showDataUpdatesModal = true;
      this.loadDataUpdates();
    },
    async loadDataUpdates() {
      this.dataUpdatesLoading = true;
      try {
        const res = await LedgerOutstandingDashboardService.getDataUpdates();
        if (res && res.data && res.data.success) {
          this.dataUpdatesRows = Array.isArray(res.data.data) ? res.data.data : [];
        } else {
          this.dataUpdatesRows = [];
        }
      } catch (e) {
        console.error("Error loading data updates", e);
        this.dataUpdatesRows = [];
      } finally {
        this.dataUpdatesLoading = false;
      }
    },
    formatUpdateDate(dateValue) {
      if (!dateValue) return "-";
      const dt = new Date(dateValue);
      if (Number.isNaN(dt.getTime())) return "-";
      return dt.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    exportDataUpdatesExcel() {
      const rows = (this.dataUpdatesRows || []).map((r) => {
        let excelDate = null;
        if (r && r.date) {
          // Keep date-only value to avoid timezone day shifts in Excel.
          const datePart = String(r.date).slice(0, 10);
          const parts = datePart.split("-");
          if (parts.length === 3) {
            const y = Number(parts[0]);
            const m = Number(parts[1]) - 1;
            const d = Number(parts[2]);
            if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
              excelDate = new Date(y, m, d);
            }
          }
        }
        return {
          Date: excelDate,
          Day: r.day_name || "-",
          "Created Count": Number(r.created_count || 0),
          "Updated Count": Number(r.updated_count || 0),
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows, { cellDates: true });
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellRef = XLSX.utils.encode_cell({ c: 0, r: row });
        if (ws[cellRef] && ws[cellRef].v instanceof Date) {
          ws[cellRef].t = "d";
          ws[cellRef].z = "dd mmm yyyy";
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data Updates");
      const ts = new Date().toISOString().replace(/[-:T.]/g, "").slice(0, 14);
      XLSX.writeFile(wb, `ledger-data-updates_${ts}.xlsx`, {
        cellDates: true,
      });
    },
    onTabChange(idx) {
      this.activeTabIndex = idx;
      this.activeTab = this.tabKeys[idx] || "summary";
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    },
    openClientInvoices(client) {
      if (!client || !client.clientId) return;
      this.selectedClient = client;
      this.clientInvoicesPage = 1;
      this.showClientInvoicesModal = true;
      this.fetchClientInvoices();
    },
    async fetchClientInvoices() {
      this.clientInvoicesLoading = true;
      try {
        const params = {
          client_id: this.selectedClient.clientId,
          page: this.clientInvoicesPage,
          pageSize: this.clientInvoicesPerPage,
        };
        const res = await LedgerOutstandingDashboardService.getList(params);
        if (res.data.success) {
          this.clientInvoices = res.data.data.records;
          this.clientInvoicesPagination.totalCount = res.data.data.pagination.totalCount;
        }
      } catch (err) {
        console.error("Error fetching client invoices:", err);
      } finally {
        this.clientInvoicesLoading = false;
      }
    },
    async exportClientInvoices() {
      if (!this.selectedClient || !this.selectedClient.clientId) return;
      try {
        const params = { client_id: this.selectedClient.clientId };
        const res = await LedgerOutstandingDashboardService.exportToExcel(params);
        if (res && res.data) {
          const blob = new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `ledger-outstanding-${this.selectedClient.clientName.replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      } catch (e) {
        console.error("Client export failed", e);
      }
    },

    async loadAllData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.loadList(),
          this.loadYearWise(),
          this.loadSummary(),
          this.loadMonthly(),
          this.loadClientWise(),
          this.loadClientWiseMonthly(),
          this.loadAgentWise(),
          this.loadAgentWiseMonthly(),
          this.loadShippingWise(),
          this.loadShippingLineWiseMonthly(),
          this.loadBranchWise(),
          this.loadBranchWiseMonthly(),
          this.loadClientPendingDays(),
          this.loadClientBranchWise(),
          this.loadAgentBranchWise(),
          this.loadShippingBranchWise(),
          this.loadBranchClientWise(),
          this.loadBranchAgentWise(),
          this.loadBranchShippingWise(),
          this.loadAssignments(),
          this.loadAllAging(),
        ]);
      } catch (e) {
        console.error("Error loading ledger dashboard data", e);
      } finally {
        this.isLoading = false;
      }
    },

    async loadSummary() {
      try {
        const res = await LedgerOutstandingDashboardService.getDashboardSummary();
        if (res && res.data && res.data.success && res.data.data && res.data.data.summary) {
          this.summary = res.data.data.summary;
        }
      } catch (e) {
        console.error("Error loading summary", e);

        console.log("Failed to load dashboard summary.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadMonthly() {
      try {
        const res = await LedgerOutstandingDashboardService.getDashboardMonthly({
          page: this.monthlyPage,
          pageSize: this.monthlyPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.monthlyRows = Array.isArray(data.records) ? data.records : [];
          if (this.monthlyRows && this.monthlyRows.length) {
            const rows = this.monthlyRows.slice();
            // ensure sorted by monthKey
            rows.sort(function (a, b) {
              const ka = String(a.monthKey || "");
              const kb = String(b.monthKey || "");
              if (ka < kb) return -1;
              if (ka > kb) return 1;
              return 0;
            });
            const categories = rows.map(function (r) {
              return r.monthKey;
            });
            const outData = rows.map(function (r) {
              return Number(r.totalOutstanding || 0);
            });
            const payData = rows.map(function (r) {
              return Number(r.totalOutstandingPayAmt || 0);
            });
            this.monthlyChartSeries = [
              { name: "Outstanding", data: outData },
              { name: "Pay Amount", data: payData },
            ];
            if (this.monthlyChartOptions && this.monthlyChartOptions.xaxis) {
              this.monthlyChartOptions.xaxis.categories = categories;
            }
          } else {
            this.monthlyChartSeries = [
              { name: "Outstanding", data: [] },
              { name: "Pay Amount", data: [] },
            ];
            if (this.monthlyChartOptions && this.monthlyChartOptions.xaxis) {
              this.monthlyChartOptions.xaxis.categories = [];
            }
          }
          if (data.pagination) {
            this.monthlyPagination = {
              currentPage: data.pagination.currentPage || this.monthlyPage,
              pageSize: data.pagination.pageSize || this.monthlyPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.monthlyRows = [];
        }
      } catch (e) {
        console.error("Error loading monthly trend", e);
        console.log("Failed to load monthly trend data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadList() {
      try {
        const res = await LedgerOutstandingDashboardService.getList({
          page: this.listPage,
          pageSize: this.listPerPage,
          search: this.globalSearch || undefined,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.listRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.listPagination = {
              currentPage: data.pagination.currentPage || this.listPage,
              pageSize: data.pagination.pageSize || this.listPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.listRows = [];
        }
      } catch (e) {
        console.error("Error loading base list", e);
        this.listRows = [];
      }
    },

    async loadYearWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getByYear(this.filterYear, {
          page: this.yearWisePage,
          pageSize: this.yearWisePerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.yearWiseRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.yearWisePagination = {
              currentPage: data.pagination.currentPage || this.yearWisePage,
              pageSize: data.pagination.pageSize || this.yearWisePerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.yearWiseRows = [];
        }
      } catch (e) {
        console.error("Error loading year-wise list", e);
        this.yearWiseRows = [];
      }
    },

    async loadClientWiseMonthly() {
      try {
        const res = await LedgerOutstandingDashboardService.getClientWiseMonthly({
          page: this.clientMonthlyPage,
          pageSize: this.clientMonthlyPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.clientMonthlyRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.clientMonthlyPagination = {
              currentPage: data.pagination.currentPage || this.clientMonthlyPage,
              pageSize: data.pagination.pageSize || this.clientMonthlyPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.clientMonthlyRows = [];
        }
      } catch (e) {
        console.error("Error loading client-wise monthly", e);
        this.clientMonthlyRows = [];
      }
    },

    async loadAgentWiseMonthly() {
      try {
        const res = await LedgerOutstandingDashboardService.getAgentWiseMonthly({
          page: this.agentMonthlyPage,
          pageSize: this.agentMonthlyPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.agentMonthlyRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.agentMonthlyPagination = {
              currentPage: data.pagination.currentPage || this.agentMonthlyPage,
              pageSize: data.pagination.pageSize || this.agentMonthlyPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.agentMonthlyRows = [];
        }
      } catch (e) {
        console.error("Error loading agent-wise monthly", e);
        this.agentMonthlyRows = [];
      }
    },

    async loadShippingLineWiseMonthly() {
      try {
        const res = await LedgerOutstandingDashboardService.getShippingLineWiseMonthly({
          page: this.shippingMonthlyPage,
          pageSize: this.shippingMonthlyPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.shippingMonthlyRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.shippingMonthlyPagination = {
              currentPage: data.pagination.currentPage || this.shippingMonthlyPage,
              pageSize: data.pagination.pageSize || this.shippingMonthlyPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.shippingMonthlyRows = [];
        }
      } catch (e) {
        console.error("Error loading shipping-line-wise monthly", e);
        this.shippingMonthlyRows = [];
      }
    },

    async loadBranchWiseMonthly() {
      try {
        const res = await LedgerOutstandingDashboardService.getBranchWiseMonthly({
          page: this.branchMonthlyPage,
          pageSize: this.branchMonthlyPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.branchMonthlyRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.branchMonthlyPagination = {
              currentPage: data.pagination.currentPage || this.branchMonthlyPage,
              pageSize: data.pagination.pageSize || this.branchMonthlyPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.branchMonthlyRows = [];
        }
      } catch (e) {
        console.error("Error loading branch-wise monthly", e);
        this.branchMonthlyRows = [];
      }
    },

    async loadClientWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getClientWise({
          page: this.clientPage,
          pageSize: this.clientPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.clientRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.clientPagination = {
              currentPage: data.pagination.currentPage || this.clientPage,
              pageSize: data.pagination.pageSize || this.clientPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.clientRows = [];
        }
      } catch (e) {
        console.error("Error loading client-wise data", e);
        console.log("Failed to load client-wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadAgentWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getAgentWise({
          page: this.agentPage,
          pageSize: this.agentPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.agentRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.agentPagination = {
              currentPage: data.pagination.currentPage || this.agentPage,
              pageSize: data.pagination.pageSize || this.agentPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.agentRows = [];
        }
      } catch (e) {
        console.error("Error loading agent-wise data", e);
        console.log("Failed to load agent-wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadShippingWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getShippingLineWise({
          page: this.shippingPage,
          pageSize: this.shippingPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.shippingRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.shippingPagination = {
              currentPage: data.pagination.currentPage || this.shippingPage,
              pageSize: data.pagination.pageSize || this.shippingPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.shippingRows = [];
        }
      } catch (e) {
        console.error("Error loading shipping-wise data", e);
        console.log("Failed to load shipping-wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadBranchWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getBranchWise({
          page: this.branchPage,
          pageSize: this.branchPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.branchRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.branchPagination = {
              currentPage: data.pagination.currentPage || this.branchPage,
              pageSize: data.pagination.pageSize || this.branchPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.branchRows = [];
        }
      } catch (e) {
        console.error("Error loading branch-wise data", e);
        console.log("Failed to load branch-wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadClientPendingDays() {
      try {
        const res = await LedgerOutstandingDashboardService.getClientWisePendingDays({
          page: this.clientPendingPage,
          pageSize: this.clientPendingPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.clientPendingRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.clientPendingPagination = {
              currentPage: data.pagination.currentPage || this.clientPendingPage,
              pageSize: data.pagination.pageSize || this.clientPendingPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.clientPendingRows = [];
        }
      } catch (e) {
        console.error("Error loading client pending days", e);
        console.log("Failed to load client pending days data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadAllAging() {
      try {
        const res = await LedgerOutstandingDashboardService.getClientAgentConsigneeWisePendingDays({
          page: this.allAgingPage,
          pageSize: this.allAgingPerPage,
          type: "all",
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.allAgingRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.allAgingPagination = {
              currentPage: data.pagination.currentPage || this.allAgingPage,
              pageSize: data.pagination.pageSize || this.allAgingPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.allAgingRows = [];
        }
      } catch (e) {
        console.error("Error loading all aging", e);
      }
    },

    async loadClientBranchWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getClientBranchWise({
          page: this.clientBranchPage,
          pageSize: this.clientBranchPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.clientBranchRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.clientBranchPagination = {
              currentPage: data.pagination.currentPage || this.clientBranchPage,
              pageSize: data.pagination.pageSize || this.clientBranchPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.clientBranchRows = [];
        }
      } catch (e) {
        console.error("Error loading client-branch wise data", e);
        console.log("Failed to load client-branch wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadAgentBranchWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getAgentBranchWise({
          page: this.agentBranchPage,
          pageSize: this.agentBranchPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.agentBranchRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.agentBranchPagination = {
              currentPage: data.pagination.currentPage || this.agentBranchPage,
              pageSize: data.pagination.pageSize || this.agentBranchPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.agentBranchRows = [];
        }
      } catch (e) {
        console.error("Error loading agent-branch wise data", e);
        console.log("Failed to load agent-branch wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadShippingBranchWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getShippingLineBranchWise({
          page: this.shippingBranchPage,
          pageSize: this.shippingBranchPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.shippingBranchRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.shippingBranchPagination = {
              currentPage: data.pagination.currentPage || this.shippingBranchPage,
              pageSize: data.pagination.pageSize || this.shippingBranchPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.shippingBranchRows = [];
        }
      } catch (e) {
        console.error("Error loading shipping-branch wise data", e);
        console.log("Failed to load shipping-branch wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadBranchClientWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getBranchClientWise({
          page: this.branchClientPage,
          pageSize: this.branchClientPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.branchClientRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.branchClientPagination = {
              currentPage: data.pagination.currentPage || this.branchClientPage,
              pageSize: data.pagination.pageSize || this.branchClientPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.branchClientRows = [];
        }
      } catch (e) {
        console.error("Error loading branch-client wise data", e);
        console.log("Failed to load branch-client wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadBranchAgentWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getBranchAgentWise({
          page: this.branchAgentPage,
          pageSize: this.branchAgentPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.branchAgentRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.branchAgentPagination = {
              currentPage: data.pagination.currentPage || this.branchAgentPage,
              pageSize: data.pagination.pageSize || this.branchAgentPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.branchAgentRows = [];
        }
      } catch (e) {
        console.error("Error loading branch-agent wise data", e);
        console.log("Failed to load branch-agent wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadBranchShippingWise() {
      try {
        const res = await LedgerOutstandingDashboardService.getBranchShippingLineWise({
          page: this.branchShippingPage,
          pageSize: this.branchShippingPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.branchShippingRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.branchShippingPagination = {
              currentPage: data.pagination.currentPage || this.branchShippingPage,
              pageSize: data.pagination.pageSize || this.branchShippingPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.branchShippingRows = [];
        }
      } catch (e) {
        console.error("Error loading branch-shipping line wise data", e);
        console.log("Failed to load branch-shipping line wise data.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    async loadAssignments() {
      try {
        const res = await LedgerOutstandingDashboardService.getAssignments({
          page: this.assignmentsPage,
          pageSize: this.assignmentsPerPage,
        });
        if (res && res.data && res.data.success && res.data.data) {
          const data = res.data.data;
          this.assignmentsRows = Array.isArray(data.records) ? data.records : [];
          if (data.pagination) {
            this.assignmentsPagination = {
              currentPage: data.pagination.currentPage || this.assignmentsPage,
              pageSize: data.pagination.pageSize || this.assignmentsPerPage,
              totalCount: data.pagination.totalCount || 0,
              totalPages: data.pagination.totalPages || 0,
            };
          }
        } else {
          this.assignmentsRows = [];
        }
      } catch (e) {
        console.error("Error loading assignments", e);
        this.assignmentsRows = [];
      }
    },

    async refreshDashboard() {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      await this.loadAllData();
      this.isRefreshing = false;
    },

    clearGlobalSearch() {
      this.globalSearch = "";
    },

    clearFilters() {
      this.filterYear = new Date().getFullYear();
      if (this.currentTabKey === "year-wise") {
        this.loadYearWise();
      }
    },

    async exportExcel() {
      try {
        const k = this.currentTabKey;
        this.isLoading = true;

        // Prepare parameters including all current filters
        const params = {
          page: 1,
          pageSize: -1,
          search: this.globalSearch || undefined,
          year: this.filterYear || undefined,
        };

        // Tab-specific filters
        const tabSearches = {
          "client-wise": this.clientSearch,
          "client-pending-days": this.clientPendingSearch,
          "client-branch-wise": this.clientBranchSearch,
          "agent-wise": this.agentSearch,
          "agent-branch-wise": this.agentBranchSearch,
          "shipping-line-wise": this.shippingSearch,
          "shipping-line-branch-wise": this.shippingBranchSearch,
          "branch-wise": this.branchSearch,
          "branch-client-wise": this.branchClientSearch,
          "branch-agent-wise": this.branchAgentSearch,
          "branch-shipping-line-wise": this.branchShippingSearch,
          "all-aging": this.allAgingSearch,
          "assignments": this.assignmentsSearch,
        };
        if (tabSearches[k]) {
          params.search = tabSearches[k];
        }

        // Base list uses dedicated backend export
        if (k === "base-list") {
          const res = await LedgerOutstandingDashboardService.exportToExcel(params);
          if (res && res.data) {
            const blob = new Blob([res.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ledger-outstanding-base-list-${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
          }
          return;
        }

        // For all other tabs, fetch FULL data (pageSize: -1) with filters
        let allData = [];
        
        const serviceMethods = {
          "year-wise": () => LedgerOutstandingDashboardService.getByYear(this.filterYear, params),
          "summary": () => LedgerOutstandingDashboardService.getDashboardMonthly(params),
          "client-wise": () => LedgerOutstandingDashboardService.getClientWise(params),
          "client-wise-monthly": () => LedgerOutstandingDashboardService.getClientWiseMonthly(params),
          "client-pending-days": () => LedgerOutstandingDashboardService.getClientWisePendingDays(params),
          "client-due-aging": () => LedgerOutstandingDashboardService.getClientWisePendingDays(params),
          "client-on-acc-aging": () => LedgerOutstandingDashboardService.getClientWisePendingDays(params),
          "client-branch-wise": () => LedgerOutstandingDashboardService.getClientBranchWise(params),
          "all-aging": () => LedgerOutstandingDashboardService.getClientAgentConsigneeWisePendingDays(Object.assign({}, params, { type: "all" })),
          "agent-wise": () => LedgerOutstandingDashboardService.getAgentWise(params),
          "agent-wise-monthly": () => LedgerOutstandingDashboardService.getAgentWiseMonthly(params),
          "agent-branch-wise": () => LedgerOutstandingDashboardService.getAgentBranchWise(params),
          "shipping-line-wise": () => LedgerOutstandingDashboardService.getShippingLineWise(params),
          "shipping-line-wise-monthly": () => LedgerOutstandingDashboardService.getShippingLineWiseMonthly(params),
          "shipping-line-branch-wise": () => LedgerOutstandingDashboardService.getShippingLineBranchWise(params),
          "branch-wise": () => LedgerOutstandingDashboardService.getBranchWise(params),
          "branch-wise-monthly": () => LedgerOutstandingDashboardService.getBranchWiseMonthly(params),
          "branch-client-wise": () => LedgerOutstandingDashboardService.getBranchClientWise(params),
          "branch-agent-wise": () => LedgerOutstandingDashboardService.getBranchAgentWise(params),
          "branch-shipping-line-wise": () => LedgerOutstandingDashboardService.getBranchShippingLineWise(params),
          "assignments": () => LedgerOutstandingDashboardService.getAssignments(params),
        };

        if (serviceMethods[k]) {
          const res = await serviceMethods[k]();
          if (res && res.data && res.data.success && res.data.data) {
            allData = Array.isArray(res.data.data.records) ? res.data.data.records : [];
          }
        } else {
          // If no specific service method, use current items (fallback)
          allData = this.currentPagedItems || [];
        }

        const exportConfig = this.buildTabExportConfig(allData);
        if (!exportConfig || !exportConfig.rows || !exportConfig.rows.length) {
          console.warn("No data to export for current tab");
          return;
        }

        this.exportRowsToXlsx(
          exportConfig.headers,
          exportConfig.rows,
          (exportConfig.fileName || "ledger-outstanding") + ".xlsx",
        );
      } catch (e) {
        console.error("Export failed", e);
      } finally {
        this.isLoading = false;
      }
    },


    toggleClientMonthlyRow(clientId) {
      const set = this.clientMonthlyExpandedSet;
      const idx = set.indexOf(clientId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(clientId);
    },

    toggleClientBranchRow(clientId) {
      const set = this.clientBranchExpandedSet;
      const idx = set.indexOf(clientId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(clientId);
    },

    toggleAgentMonthlyRow(agentId) {
      const set = this.agentMonthlyExpandedSet;
      const idx = set.indexOf(agentId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(agentId);
    },

    toggleAgentBranchRow(agentId) {
      const set = this.agentBranchExpandedSet;
      const idx = set.indexOf(agentId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(agentId);
    },

    toggleShippingBranchRow(shippingLineId) {
      const set = this.shippingBranchExpandedSet;
      const idx = set.indexOf(shippingLineId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(shippingLineId);
    },

    toggleShippingMonthlyRow(shippingLineId) {
      const set = this.shippingMonthlyExpandedSet;
      const idx = set.indexOf(shippingLineId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(shippingLineId);
    },

    toggleBranchMonthlyRow(branchId) {
      const set = this.branchMonthlyExpandedSet;
      const idx = set.indexOf(branchId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(branchId);
    },

    toggleBranchClientRow(branchId) {
      const set = this.branchClientExpandedSet;
      const idx = set.indexOf(branchId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(branchId);
    },

    toggleBranchAgentRow(branchId) {
      const set = this.branchAgentExpandedSet;
      const idx = set.indexOf(branchId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(branchId);
    },

    toggleBranchShippingRow(branchId) {
      if (!this.branchShippingExpandedSet) this.branchShippingExpandedSet = [];
      const set = this.branchShippingExpandedSet;
      const idx = set.indexOf(branchId);
      if (idx >= 0) set.splice(idx, 1);
      else set.push(branchId);
    },

    expandChevronDown(row) {
      const k = this.currentTabKey;
      if (k === "client-wise-monthly") return (this.clientMonthlyExpandedSet || []).indexOf(row.item.clientId) >= 0;
      if (k === "client-branch-wise") return (this.clientBranchExpandedSet || []).indexOf(row.item.clientId) >= 0;
      if (k === "agent-wise-monthly") return (this.agentMonthlyExpandedSet || []).indexOf(row.item.agentId) >= 0;
      if (k === "agent-branch-wise") return (this.agentBranchExpandedSet || []).indexOf(row.item.agentId) >= 0;
      if (k === "shipping-line-wise-monthly") return (this.shippingMonthlyExpandedSet || []).indexOf(row.item.shippingLineId) >= 0;
      if (k === "branch-wise-monthly") return (this.branchMonthlyExpandedSet || []).indexOf(row.item.branchId) >= 0;
      if (k === "branch-client-wise") return (this.branchClientExpandedSet || []).indexOf(row.item.branchId) >= 0;
      if (k === "branch-agent-wise") return (this.branchAgentExpandedSet || []).indexOf(row.item.branchId) >= 0;
      if (k === "branch-shipping-line-wise") return (this.branchShippingExpandedSet || []).indexOf(row.item.branchId) >= 0;
      if (k === "shipping-line-branch-wise") return (this.shippingBranchExpandedSet || []).indexOf(row.item.shippingLineId) >= 0;
      return false;
    },

    monthlyDataAsRows(monthlyData) {
      if (!monthlyData || typeof monthlyData !== "object") return [];
      return Object.keys(monthlyData)
        .sort()
        .map((monthKey) => ({
          monthKey,
          totalOutstanding: monthlyData[monthKey].totalOutstanding,
          totalOutstandingPayAmt: monthlyData[monthKey].totalOutstandingPayAmt,
          totalInvoices: monthlyData[monthKey].totalInvoices,
        }));
    },

    formatNumber(value) {
      const num = parseFloat(value || 0);
      return num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatCurrency(value) {
      const num = parseFloat(value || 0);
      return num.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    detailMonthlyChartConfig(monthlyData) {
      if (!monthlyData || typeof monthlyData !== "object") return null;
      const keys = Object.keys(monthlyData).sort();
      if (!keys.length) return null;

      const categories = [];
      const outData = [];
      const payData = [];

      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        const rec = monthlyData[k] || {};
        categories.push(k);
        outData.push(Number(rec.totalOutstanding || 0));
        payData.push(Number(rec.totalOutstandingPayAmt || 0));
      }

      const options = {
        chart: {
          height: 180,
          type: "line",
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 0,
          hover: { sizeOffset: 3 },
        },
        xaxis: {
          categories,
          labels: { rotate: -45 },
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              var num = parseFloat(val || 0);
              return num.toLocaleString("en-IN");
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              var num = parseFloat(val || 0);
              return num.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
        title: {
          text: "Monthly Outstanding",
          align: "left",
        },
      };

      const series = [
        { name: "Outstanding", data: outData },
        { name: "Pay Amount", data: payData },
      ];

      return { options, series };
    },

    detailChartConfig(kind, items) {
      if (!items || !items.length) return null;
      const map = {
        clientBranch: { xKey: "branchLocation", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Branch" },
        agentBranch: { xKey: "branchLocation", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Branch" },
        shippingBranch: { xKey: "branchLocation", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Branch" },
        branchClient: { xKey: "clientName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Client" },
        branchAgent: { xKey: "agentName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Agent" },
        branchShipping: { xKey: "shippingLineName", outKey: "totalOutstanding", payKey: "totalOutstandingPayAmt", title: "Outstanding by Shipping Line" },
      };
      const cfg = map[kind];
      if (!cfg) return null;

      const categories = [];
      const outData = [];
      const payData = [];
      for (let i = 0; i < items.length; i++) {
        const r = items[i];
        const xVal = r[cfg.xKey];
        if (xVal == null || xVal === "") continue;
        categories.push(String(xVal));
        outData.push(Number(r[cfg.outKey] || 0));
        payData.push(Number(r[cfg.payKey] || 0));
      }
      if (!categories.length) return null;

      const options = {
        chart: {
          height: 180,
          type: "bar",
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: {
            columnWidth: "55%",
          },
        },
        xaxis: {
          categories,
          labels: { rotate: -45 },
        },
        yaxis: {
          labels: {
            formatter: function (val) {
              var num = parseFloat(val || 0);
              return num.toLocaleString("en-IN");
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              var num = parseFloat(val || 0);
              return num.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
        title: {
          text: cfg.title || "",
          align: "left",
        },
      };

      const series = [
        { name: "Outstanding", data: outData },
        { name: "Pay Amount", data: payData },
      ];

      return { options, series };
    },

    exportRowsToXlsx(headers, rows, fileName) {
      const data = [];
      if (rows && rows.length) {
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const obj = {};
          const keys = headers || Object.keys(row);
          for (let j = 0; j < keys.length; j++) {
            const key = keys[j];
            obj[key] = row[key];
          }
          data.push(obj);
        }
      }
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Ledger");
      const ts = new Date().toISOString().replace(/[-:T.]/g, "").slice(0, 14);
      const finalName =
        (fileName || "ledger-outstanding-export.xlsx").replace(".xlsx", "") +
        "_" +
        ts +
        ".xlsx";
      XLSX.writeFile(wb, finalName);
    },

    buildTabExportConfig(externalData = null) {
      const k = this.currentTabKey;
      const dateStr = new Date().toISOString().slice(0, 10);

      function buildFlat(list, headers, mapper, fileName) {
        const rows = [];
        if (list && list.length) {
          for (let i = 0; i < list.length; i++) {
            rows.push(mapper(list[i], i));
          }
        }
        return { headers, rows, fileName: fileName + "-" + k + "-" + dateStr };
      }

      // Helper to flatten nested arrays (accordion data)
      function buildNested(list, childKey, headers, mapper, fileName) {
        const rows = [];
        if (list && list.length) {
          for (let i = 0; i < list.length; i++) {
            const parent = list[i];
            const children = parent[childKey] || [];
            if (!children.length) {
              rows.push(mapper(parent, null, i));
              continue;
            }
            for (let j = 0; j < children.length; j++) {
              rows.push(mapper(parent, children[j], i, j));
            }
          }
        }
        return { headers, rows, fileName: fileName + "-" + k + "-" + dateStr };
      }

      // Switch per tab
      if (k === "client-wise") {
        return buildFlat(
          externalData || this.clientRows,
          ["Client ID", "Client Name", "Total Outstanding", "Total Pay Amount", "Total Invoices"],
          function (r) {
            return {
              "Client ID": r.clientId,
              "Client Name": r.clientName,
              "Total Outstanding": r.totalOutstanding,
              "Total Pay Amount": r.totalOutstandingPayAmt,
              "Total Invoices": r.totalInvoices,
            };
          },
          "ledger-outstanding-client-wise",
        );
      }

      if (k === "client-wise-monthly") {
        const rows = [];
        const list = externalData || this.clientMonthlyRows || [];
        for (let i = 0; i < list.length; i++) {
          const parent = list[i];
          const md = parent.monthlyData || {};
          const keys = Object.keys(md).sort();
          if (!keys.length) {
            rows.push({
              "Client ID": parent.clientId,
              "Client Name": parent.clientName,
              Month: "",
              "Outstanding": "",
              "Pay Amount": "",
              "Invoices": "",
            });
            continue;
          }
          for (let j = 0; j < keys.length; j++) {
            const mk = keys[j];
            const rec = md[mk] || {};
            rows.push({
              "Client ID": parent.clientId,
              "Client Name": parent.clientName,
              Month: mk,
              "Outstanding": rec.totalOutstanding,
              "Pay Amount": rec.totalOutstandingPayAmt,
              "Invoices": rec.totalInvoices,
            });
          }
        }
        return {
          headers: ["Client ID", "Client Name", "Month", "Outstanding", "Pay Amount", "Invoices"],
          rows,
          fileName: "ledger-outstanding-client-wise-monthly-" + dateStr,
        };
      }

      if (k === "client-pending-days") {
        return buildFlat(
          externalData || this.clientPendingRows,
          [
            "Client ID",
            "Client Name",
            "Total Invoices",
            "Pending 0-30",
            "Pending 31-60",
            "Pending 61-90",
            "Pending 90+",
            "Pending Total",
          ],
          function (r) {
            return {
              "Client ID": r.clientId,
              "Client Name": r.clientName,
              "Total Invoices": r.totalInvoices,
              "Credit Days": r.creditDays,
              "Net Outstanding": r.net_amount,
              "On Account AMT": r.on_acc_amt,
              "Due AMT": r.due_amount,
              "Outstanding AMT": r.pending_total,
              "Pending 0-30": r.pending_0_30,
              "Pending 31-60": r.pending_31_60,
              "Pending 61-90": r.pending_61_90,
              "Pending 90+": r.pending_90_plus,
              "Pending Total": r.pending_total,

              "Total Pay Amount": r.totalOutstandingPayAmt,
              "Pay Amount 0-30": r.totalOutstandingPayAmt0_30,
              "Pay Amount 31-60": r.totalOutstandingPayAmt31_60,
              "Pay Amount 61-90": r.totalOutstandingPayAmt61_90,
              "Pay Amount 90+": r.totalOutstandingPayAmt90Plus,

              "Due 0-30": r.due_0_30,
              "Due 31-60": r.due_31_60,
              "Due 61-90": r.due_61_90,
              "Due 90+": r.due_90_plus,

              "On Account 0-30": r.on_acc_amt_0_30,
              "On Account 31-60": r.on_acc_amt_31_60,
              "On Account 61-90": r.on_acc_amt_61_90,
              "On Account 90+": r.on_acc_amt_90_plus,

              "Due On Account 0-30": r.due_on_acc_amt_0_30,
              "Due On Account 31-60": r.due_on_acc_amt_31_60,
              "Due On Account 61-90": r.due_on_acc_amt_61_90,
              "Due On Account 90+": r.due_on_acc_amt_90_plus,

            };
          },
          "ledger-outstanding-client-pending-days",
        );
      }

      if (k === "client-branch-wise") {
        return buildNested(
          externalData || this.clientBranchRows,
          "branch",
          [
            "Client ID",
            "Client Name",
            "Branch ID",
            "Branch",
            "Main Location",
            "Branch Outstanding",
            "Branch Pay Amount",
            "Branch Invoices",
          ],
          function (parent, child) {
            if (!child) {
              return {
                "Client ID": parent.clientId,
                "Client Name": parent.clientName,
                "Branch ID": "",
                Branch: "",
                "Main Location": "",
                "Branch Outstanding": "",
                "Branch Pay Amount": "",
                "Branch Invoices": "",
              };
            }
            return {
              "Client ID": parent.clientId,
              "Client Name": parent.clientName,
              "Branch ID": child.branchId,
              Branch: child.branchLocation,
              "Main Location": child.branchMainLocation,
              "Branch Outstanding": child.totalOutstanding,
              "Branch Pay Amount": child.totalOutstandingPayAmt,
              "Branch Invoices": child.totalInvoices,
            };
          },
          "ledger-outstanding-client-branch-wise",
        );
      }

      if (k === "agent-wise") {
        return buildFlat(
          externalData || this.agentRows,
          [
            "Agent ID",
            "Agent Name",
            "Co-Loader",
            "Company",
            "Total Outstanding",
            "Total Pay Amount",
            "Total Invoices",
          ],
          function (r) {
            return {
              "Agent ID": r.agentId,
              "Agent Name": r.agentName,
              "Co-Loader": r.coLoaderName,
              Company: r.companyName,
              "Total Outstanding": r.totalOutstanding,
              "Total Pay Amount": r.totalOutstandingPayAmt,
              "Total Invoices": r.totalInvoices,
            };
          },
          "ledger-outstanding-agent-wise",
        );
      }

      if (k === "agent-wise-monthly") {
        const rows = [];
        const list = externalData || this.agentMonthlyRows || [];
        for (let i = 0; i < list.length; i++) {
          const parent = list[i];
          const md = parent.monthlyData || {};
          const keys = Object.keys(md).sort();
          if (!keys.length) {
            rows.push({
              "Agent ID": parent.agentId,
              "Agent Name": parent.agentName,
              Month: "",
              "Outstanding": "",
              "Pay Amount": "",
              "Invoices": "",
            });
            continue;
          }
          for (let j = 0; j < keys.length; j++) {
            const mk = keys[j];
            const rec = md[mk] || {};
            rows.push({
              "Agent ID": parent.agentId,
              "Agent Name": parent.agentName,
              Month: mk,
              "Outstanding": rec.totalOutstanding,
              "Pay Amount": rec.totalOutstandingPayAmt,
              "Invoices": rec.totalInvoices,
            });
          }
        }
        return {
          headers: ["Agent ID", "Agent Name", "Month", "Outstanding", "Pay Amount", "Invoices"],
          rows,
          fileName: "ledger-outstanding-agent-wise-monthly-" + dateStr,
        };
      }

      if (k === "agent-branch-wise") {
        return buildNested(
          externalData || this.agentBranchRows,
          "branch",
          [
            "Agent ID",
            "Agent Name",
            "Branch ID",
            "Branch",
            "Main Location",
            "Branch Outstanding",
            "Branch Pay Amount",
            "Branch Invoices",
          ],
          function (parent, child) {
            if (!child) {
              return {
                "Agent ID": parent.agentId,
                "Agent Name": parent.agentName,
                "Branch ID": "",
                Branch: "",
                "Main Location": "",
                "Branch Outstanding": "",
                "Branch Pay Amount": "",
                "Branch Invoices": "",
              };
            }
            return {
              "Agent ID": parent.agentId,
              "Agent Name": parent.agentName,
              "Branch ID": child.branchId,
              Branch: child.branchLocation,
              "Main Location": child.branchMainLocation,
              "Branch Outstanding": child.totalOutstanding,
              "Branch Pay Amount": child.totalOutstandingPayAmt,
              "Branch Invoices": child.totalInvoices,
            };
          },
          "ledger-outstanding-agent-branch-wise",
        );
      }

      if (k === "shipping-line-wise") {
        return buildFlat(
          externalData || this.shippingRows,
          [
            "Shipping Line ID",
            "Shipping Line",
            "Total Outstanding",
            "Total Pay Amount",
            "Total Invoices",
          ],
          function (r) {
            return {
              "Shipping Line ID": r.shippingLineId,
              "Shipping Line": r.shippingLineName,
              "Total Outstanding": r.totalOutstanding,
              "Total Pay Amount": r.totalOutstandingPayAmt,
              "Total Invoices": r.totalInvoices,
            };
          },
          "ledger-outstanding-shipping-line-wise",
        );
      }

      if (k === "shipping-line-wise-monthly") {
        const rows = [];
        const list = externalData || this.shippingMonthlyRows || [];
        for (let i = 0; i < list.length; i++) {
          const parent = list[i];
          const md = parent.monthlyData || {};
          const keys = Object.keys(md).sort();
          if (!keys.length) {
            rows.push({
              "Shipping Line ID": parent.shippingLineId,
              "Shipping Line": parent.shippingLineName,
              Month: "",
              "Outstanding": "",
              "Pay Amount": "",
              "Invoices": "",
            });
            continue;
          }
          for (let j = 0; j < keys.length; j++) {
            const mk = keys[j];
            const rec = md[mk] || {};
            rows.push({
              "Shipping Line ID": parent.shippingLineId,
              "Shipping Line": parent.shippingLineName,
              Month: mk,
              "Outstanding": rec.totalOutstanding,
              "Pay Amount": rec.totalOutstandingPayAmt,
              "Invoices": rec.totalInvoices,
            });
          }
        }
        return {
          headers: [
            "Shipping Line ID",
            "Shipping Line",
            "Month",
            "Outstanding",
            "Pay Amount",
            "Invoices",
          ],
          rows,
          fileName: "ledger-outstanding-shipping-line-wise-monthly-" + dateStr,
        };
      }

      if (k === "shipping-line-branch-wise") {
        return buildNested(
          externalData || this.shippingBranchRows,
          "branch",
          [
            "Shipping Line ID",
            "Shipping Line",
            "Branch ID",
            "Branch",
            "Main Location",
            "Branch Outstanding",
            "Branch Pay Amount",
            "Branch Invoices",
          ],
          function (parent, child) {
            if (!child) {
              return {
                "Shipping Line ID": parent.shippingLineId,
                "Shipping Line": parent.shippingLineName,
                "Branch ID": "",
                Branch: "",
                "Main Location": "",
                "Branch Outstanding": "",
                "Branch Pay Amount": "",
                "Branch Invoices": "",
              };
            }
            return {
              "Shipping Line ID": parent.shippingLineId,
              "Shipping Line": parent.shippingLineName,
              "Branch ID": child.branchId,
              Branch: child.branchLocation,
              "Main Location": child.branchMainLocation,
              "Branch Outstanding": child.totalOutstanding,
              "Branch Pay Amount": child.totalOutstandingPayAmt,
              "Branch Invoices": child.totalInvoices,
            };
          },
          "ledger-outstanding-shipping-line-branch-wise",
        );
      }

      if (k === "branch-wise") {
        return buildFlat(
          externalData || this.branchRows,
          [
            "Branch ID",
            "Branch",
            "Main Location",
            "Total Outstanding",
            "Total Pay Amount",
            "Total Invoices",
          ],
          function (r) {
            return {
              "Branch ID": r.branchId,
              Branch: r.branchLocation,
              "Main Location": r.branchMainLocation,
              "Total Outstanding": r.totalOutstanding,
              "Total Pay Amount": r.totalOutstandingPayAmt,
              "Total Invoices": r.totalInvoices,
            };
          },
          "ledger-outstanding-branch-wise",
        );
      }

      if (k === "branch-wise-monthly") {
        const rows = [];
        const list = externalData || this.branchMonthlyRows || [];
        for (let i = 0; i < list.length; i++) {
          const parent = list[i];
          const md = parent.monthlyData || {};
          const keys = Object.keys(md).sort();
          if (!keys.length) {
            rows.push({
              "Branch ID": parent.branchId,
              Branch: parent.branchLocation,
              Month: "",
              "Outstanding": "",
              "Pay Amount": "",
              "Invoices": "",
            });
            continue;
          }
          for (let j = 0; j < keys.length; j++) {
            const mk = keys[j];
            const rec = md[mk] || {};
            rows.push({
              "Branch ID": parent.branchId,
              Branch: parent.branchLocation,
              Month: mk,
              "Outstanding": rec.totalOutstanding,
              "Pay Amount": rec.totalOutstandingPayAmt,
              "Invoices": rec.totalInvoices,
            });
          }
        }
        return {
          headers: ["Branch ID", "Branch", "Month", "Outstanding", "Pay Amount", "Invoices"],
          rows,
          fileName: "ledger-outstanding-branch-wise-monthly-" + dateStr,
        };
      }

      if (k === "branch-client-wise") {
        return buildNested(
          externalData || this.branchClientRows,
          "client",
          [
            "Branch ID",
            "Branch",
            "Main Location",
            "Client ID",
            "Client Name",
            "Client Outstanding",
            "Client Pay Amount",
            "Client Invoices",
          ],
          function (parent, child) {
            if (!child) {
              return {
                "Branch ID": parent.branchId,
                Branch: parent.branchLocation,
                "Main Location": parent.branchMainLocation,
                "Client ID": "",
                "Client Name": "",
                "Client Outstanding": "",
                "Client Pay Amount": "",
                "Client Invoices": "",
              };
            }
            return {
              "Branch ID": parent.branchId,
              Branch: parent.branchLocation,
              "Main Location": parent.branchMainLocation,
              "Client ID": child.clientId,
              "Client Name": child.clientName,
              "Client Outstanding": child.totalOutstanding,
              "Client Pay Amount": child.totalOutstandingPayAmt,
              "Client Invoices": child.totalInvoices,
            };
          },
          "ledger-outstanding-branch-client-wise",
        );
      }

      if (k === "branch-agent-wise") {
        return buildNested(
          externalData || this.branchAgentRows,
          "agent",
          [
            "Branch ID",
            "Branch",
            "Main Location",
            "Agent ID",
            "Agent Name",
            "Agent Outstanding",
            "Agent Pay Amount",
            "Agent Invoices",
          ],
          function (parent, child) {
            if (!child) {
              return {
                "Branch ID": parent.branchId,
                Branch: parent.branchLocation,
                "Main Location": parent.branchMainLocation,
                "Agent ID": "",
                "Agent Name": "",
                "Agent Outstanding": "",
                "Agent Pay Amount": "",
                "Agent Invoices": "",
              };
            }
            return {
              "Branch ID": parent.branchId,
              Branch: parent.branchLocation,
              "Main Location": parent.branchMainLocation,
              "Agent ID": child.agentId,
              "Agent Name": child.agentName,
              "Agent Outstanding": child.totalOutstanding,
              "Agent Pay Amount": child.totalOutstandingPayAmt,
              "Agent Invoices": child.totalInvoices,
            };
          },
          "ledger-outstanding-branch-agent-wise",
        );
      }

      if (k === "branch-shipping-line-wise") {
        return buildNested(
          externalData || this.branchShippingRows,
          "shippingLine",
          [
            "Branch ID",
            "Branch",
            "Main Location",
            "Shipping Line ID",
            "Shipping Line",
            "Shipping Outstanding",
            "Shipping Pay Amount",
            "Shipping Invoices",
          ],
          function (parent, child) {
            if (!child) {
              return {
                "Branch ID": parent.branchId,
                Branch: parent.branchLocation,
                "Main Location": parent.branchMainLocation,
                "Shipping Line ID": "",
                "Shipping Line": "",
                "Shipping Outstanding": "",
                "Shipping Pay Amount": "",
                "Shipping Invoices": "",
              };
            }
            return {
              "Branch ID": parent.branchId,
              Branch: parent.branchLocation,
              "Main Location": parent.branchMainLocation,
              "Shipping Line ID": child.shippingLineId,
              "Shipping Line": child.shippingLineName,
              "Shipping Outstanding": child.totalOutstanding,
              "Shipping Pay Amount": child.totalOutstandingPayAmt,
              "Shipping Invoices": child.totalInvoices,
            };
          },
          "ledger-outstanding-branch-shipping-line-wise",
        );
      }

      if (k === "year-wise") {
        return buildFlat(
          externalData || this.yearWiseRows,
          [
            "ID",
            "Ledger Name",
            "Doc Date",
            "Pending",
            "Pay Amt",
            "Client Name",
          ],
          function (r) {
            return {
              ID: r.lom_id,
              "Ledger Name": r.lom_ledger_name,
              "Doc Date": r.lom_doc_date,
              Pending: r.lom_pending_amt,
              "Pay Amt": r.lom_pay_amt,
              "Client Name": r.clientName,
            };
          },
          "ledger-outstanding-year-wise",
        );
      }

      if (k === "summary") {
        return buildFlat(
          externalData || this.monthlyRows,
          ["Month", "Outstanding", "Pay Amount", "Invoices"],
          function (r) {
            return {
              Month: r.monthKey,
              Outstanding: r.totalOutstanding,
              "Pay Amount": r.totalOutstandingPayAmt,
              Invoices: r.totalInvoices,
            };
          },
          "ledger-outstanding-summary-monthly",
        );
      }

      if (k === "assignments") {
        return buildFlat(
          externalData || this.assignmentsRows,
          ["Job No", "Client", "Branch", "Module", "Pending Amount"],
          function (r) {
            return {
              "Job No": r.jobNo,
              Client: r.clientName,
              Branch: r.branchName,
              Module: r.moduleType,
              "Pending Amount": r.pendingAmount,
            };
          },
          "ledger-outstanding-assignments",
        );
      }

      if (k === "all-aging") {
        return buildFlat(
          externalData || this.allAgingRows,
          [
            "Type",
            "Name",
            "Invoices",
            "Credit Days",
            "Net Outstanding",
            "On Account",
            "Pending Total",
            "Due",
            "0-30 Days",
            "31-60 Days",
            "61-90 Days",
            "90+ Days",
          ],
          function (r) {
            return {
              "Type": r.debtorType,
              "Name": r.clientName,
              "Invoices": r.totalInvoices,
              "Credit Days": r.creditDays,
              "Net Outstanding": r.net_amount,
              "On Account": r.on_acc_amt,
              "Pending Total": r.pending_total,
              "Due": r.due_amount,
              "0-30 Days": r.pending_0_30,
              "31-60 Days": r.pending_31_60,
              "61-90 Days": r.pending_61_90,
              "90+ Days": r.pending_90_plus,
            };
          },
          "ledger-outstanding-all-aging",
        );
      }

      // Fallback: export current table rows as-is using current fields
      const items = externalData || this.currentPagedItems || [];
      if (!items.length) return null;
      const fields = this.currentFields || [];
      const headers = fields.map(function (f) {
        return f.label || f.key;
      });
      const rows = [];
      for (let i = 0; i < items.length; i++) {
        const r = items[i];
        const obj = {};
        for (let j = 0; j < fields.length; j++) {
          const f = fields[j];
          const key = f.key;
          obj[headers[j]] = r[key];
        }
        rows.push(obj);
      }
      return {
        headers,
        rows,
        fileName: "ledger-outstanding-generic-" + dateStr,
      };
    },

    onUploadSuccess() {
      this.showImportModal = false;
      this.refreshDashboard();
      console.log("Data imported successfully!", {
        title: "Success",
        variant: "success",
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
/* Mobile responsiveness and aesthetics */
@media (max-width: 768px) {
  .growth-dashboard-container {
    padding: 10px;
  }
  
  .page-inner-title {
    font-size: 1.25rem;
  }
  
  .search-input-group {
    width: 100% !important;
  }
  
  .action-buttons-group {
    width: 100%;
  }
  
  .summary-card h5 {
    font-size: 0.95rem !important;
  }
  
  .card-icon {
    width: 28px !important;
    height: 28px !important;
    font-size: 0.75rem;
    margin-right: 5px !important;
  }

  .currency-text {
    font-size: 0.95rem !important;
  }
}

.currency-text {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  letter-spacing: -0.5px;
}

.tabs-scroll-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.premium-tabs {
  min-width: max-content;
}

::v-deep .premium-tabs .nav-pills .nav-link {
  border-radius: 20px !important;
  padding: 8px 15px;
  font-weight: 500;
  color: #6c757d !important;
  margin-right: 5px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

::v-deep .premium-tabs .nav-pills .nav-link:hover {
  background: #f0f2f5;
  color: #3f51b5 !important;
}

::v-deep .premium-tabs .nav-pills .nav-link.active {
  background-color: #3f51b5 !important;
  color: white !important;
  box-shadow: 0 4px 10px rgba(63, 81, 181, 0.3);
}

/* Gradients for Premium Look */
.gradient-info { background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%); }
.gradient-warning { background: linear-gradient(135deg, #ffffff 0%, #fffbf0 100%); }
.gradient-primary { background: linear-gradient(135deg, #ffffff 0%, #f5f7ff 100%); }
.gradient-danger { background: linear-gradient(135deg, #ffffff 0%, #fff5f5 100%); }
.gradient-success { background: linear-gradient(135deg, #ffffff 0%, #f6fff6 100%); }
.gradient-info-light { background: linear-gradient(135deg, #ffffff 0%, #f0fcff 100%); }
.gradient-warning-light { background: linear-gradient(135deg, #ffffff 0%, #fffdf5 100%); }
.gradient-secondary { background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%); }
.gradient-dark { background: linear-gradient(135deg, #ffffff 0%, #f1f3f5 100%); }

.summary-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  border: 1px solid rgba(0,0,0,0.05) !important;
}

.summary-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
}

.card-icon {
  width: 40px;
  height: 40px;
  transition: all 0.3s ease;
}

.summary-card:hover .card-icon {
  transform: scale(1.1);
}

.growth-dashboard-container {
  padding: 15px;
}

.page-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-icon {
  color: #3f51b5;
}

.summary-card .card-title {
  white-space: nowrap;
}

/* Table Style improvements */
.custom-table thead th {
  border-top: none;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  color: #495057;
  white-space: nowrap;
}

.custom-table ::v-deep tbody tr {
  transition: background-color 0.2s ease;
}

.custom-table ::v-deep tbody tr:hover {
  background-color: #f8f9fa;
}

/* Sticky Header Fix */
::v-deep .b-table-sticky-header {
  overflow: auto !important;
}

::v-deep .b-table-sticky-header thead th,
::v-deep .b-table-sticky-header thead {
  position: sticky !important;
  top: 0 !important;
  z-index: 100 !important;
  background-color: #f8f9fa !important;
  box-shadow: inset 0 -1px 0 #dee2e6;
}

::v-deep .b-table-sticky-header table {
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

::v-deep .b-table-sticky-header thead th {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
}
</style>




// WEBPACK FOOTER //
// ledgerOutstandingDashboard.vue?d2d05fe2